/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { UserProfileRepository } from "../storage/repositories/UserProfileRepository";
import { PaymentGatewayFactory } from "./PaymentGatewayFactory";
import { FinancialGateway } from "./FinancialGateway";
import { CommercialPlanService } from "./CommercialPlanService";
import { PaymentRequest } from "./types/financial.types";
import {
  CreateSubscriptionRequest,
  SubscriptionResponse,
  CancelSubscriptionResponse,
  UpdateSubscriptionRequest,
  ListSubscriptionPaymentsResponse,
  SubscriptionRecord,
  SubscriptionPaymentRecord,
  ArenaMonthlyCycle,
  ArenaCycleStatus,
  SubscriptionCycleRecord,
} from "./types/subscription.types";
import { ProportionalFeeCalculator, ProportionalCalculationResult } from "./ProportionalFeeCalculator";
import { resolveArenaFinancialModelSync, saveArenaFinancialConfig } from "./FinancialModelResolver";
import { resolveOperationalCommercialCycle, OperationalCycleResolution } from "./OperationalCycleResolver";
import { syncOperationalCourtCapacity, OperationalCapacitySyncResult } from "./OperationalCourtCapacityService";
import { CourtItem } from "../../types/userProfile";
import { ArenaFinancialModelType } from "../../types/commercialRules";
import { StorageService } from "../storage/StorageService";
import { DEFAULT_COMMERCIAL_PLANS } from "../../utils/commissionConfig";
import { db, isFirebaseConfigured, getAuthHeaders } from "../../utils/firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  writeBatch,
  Firestore,
} from "firebase/firestore";

// Fallback in-memory storage for subscriptions when running in offline or test mode
const inMemorySubscriptions: Map<string, SubscriptionRecord> = new Map();
const inMemorySubscriptionPayments: Map<string, SubscriptionPaymentRecord[]> = new Map();
const inMemorySubscriptionCycles: Map<string, SubscriptionCycleRecord[]> = new Map();
const inMemoryConfirmedPDVPayments: Map<string, any> = new Map();
const processedWebhookEventIds: Set<string> = new Set();
const processedRenewalPaymentIds: Set<string> = new Set();

function isV8Db(target: any): boolean {
  return Boolean(target && typeof target.collection === "function");
}

function resolveModularDb(target: any): Firestore | null {
  if (target && typeof target === "object" && typeof target.collection !== "function") {
    return target as Firestore;
  }
  return null;
}

function isPermissionDeniedError(err: any): boolean {
  if (!err) return false;
  const code = String(err.code || "");
  const msg = String(err.message || "");
  return (
    code === "permission-denied" ||
    code === "7" ||
    msg.includes("permission-denied") ||
    msg.includes("PERMISSION_DENIED") ||
    msg.includes("Missing or insufficient permissions")
  );
}

async function getFirestoreDoc(
  dbTarget: any,
  collectionName: string,
  docId: string
): Promise<{ exists: boolean; data?: any }> {
  if (!dbTarget || !docId) return { exists: false };

  if (isV8Db(dbTarget)) {
    const snap = await dbTarget.collection(collectionName).doc(docId).get();
    if (!snap) return { exists: false };
    const exists = typeof snap.exists === "function" ? snap.exists() : snap.exists;
    const data = typeof snap.data === "function" ? snap.data() : snap.data;
    return { exists: Boolean(exists || data), data };
  }

  const modularDb = resolveModularDb(dbTarget);
  if (modularDb) {
    const docRef = doc(modularDb, collectionName, docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return { exists: false };
    return { exists: true, data: snap.data() };
  }

  return { exists: false };
}

async function setFirestoreDoc(
  dbTarget: any,
  collectionName: string,
  docId: string,
  data: any,
  merge: boolean = true
): Promise<void> {
  if (!dbTarget || !docId) return;

  if (isV8Db(dbTarget)) {
    await dbTarget.collection(collectionName).doc(docId).set(data, { merge });
    return;
  }

  const modularDb = resolveModularDb(dbTarget);
  if (modularDb) {
    const docRef = doc(modularDb, collectionName, docId);
    await setDoc(docRef, data, { merge });
  }
}

async function queryFirestoreDocs(
  dbTarget: any,
  collectionName: string,
  filters: Array<{ field: string; op: any; value: any }>,
  limitCount?: number
): Promise<any[]> {
  if (!dbTarget) return [];

  if (isV8Db(dbTarget)) {
    let q = dbTarget.collection(collectionName);
    for (const f of filters) {
      q = q.where(f.field, f.op, f.value);
    }
    if (limitCount) {
      q = q.limit(limitCount);
    }
    const snap = await q.get();
    if (!snap || snap.empty || !snap.docs) return [];
    return snap.docs.map((d: any) => {
      const data = typeof d.data === "function" ? d.data() : d.data;
      return { ...data, id: d.id || data.id, ref: d.ref };
    });
  }

  const modularDb = resolveModularDb(dbTarget);
  if (modularDb) {
    const colRef = collection(modularDb, collectionName);
    const constraints: any[] = filters.map(f => where(f.field, f.op, f.value));
    if (limitCount) {
      constraints.push(limit(limitCount));
    }
    const q = query(colRef, ...constraints);
    const snap = await getDocs(q);
    if (!snap || snap.empty || !snap.docs) return [];
    return snap.docs.map(d => ({ ...d.data(), id: d.id, ref: d.ref }));
  }

  return [];
}

export class SubscriptionService {
  /**
   * Obtém a instância de FinancialGateway adequada para operações de recorrência.
   */
  private static getGateway(gatewayName?: string): FinancialGateway {
    return PaymentGatewayFactory.getFinancialGateway(gatewayName);
  }

  /**
   * Resolve dados cadastrais reais da Arena/Proprietário sem nunca inventar nomes, e-mails ou CPFs fictícios.
   * NUNCA inventa: nome, email, CPF/CNPJ, telefone ou customerId.
   */
  static async resolveRealCustomerData(
    arenaId: string,
    firestoreDb?: any,
    requestHint?: { name?: string; email?: string; cpfCnpj?: string; phone?: string; customerId?: string }
  ): Promise<{
    name: string;
    email: string;
    cpfCnpj?: string;
    phone?: string;
    customerId?: string;
    isValid: boolean;
    missingFields: string[];
  }> {
    let name = requestHint?.name?.trim() || "";
    let email = requestHint?.email?.trim() || "";
    let cpfCnpj = requestHint?.cpfCnpj?.trim() || "";
    let phone = requestHint?.phone?.trim() || "";
    let customerId = requestHint?.customerId?.trim() || "";

    const isPlaceholderEmail = (em?: string) =>
      !em ||
      !em.includes("@") ||
      !em.includes(".") ||
      em.toLowerCase() === "arena@atletahub.com.br" ||
      em.toLowerCase().includes("placeholder") ||
      em.toLowerCase() === "system" ||
      (Boolean(arenaId) && em.startsWith("contato@") && em.includes(arenaId)) ||
      (Boolean(arenaId) && em.startsWith("financeiro@") && em.includes(arenaId));

    const isPlaceholderName = (nm?: string) =>
      !nm ||
      nm.trim().length < 2 ||
      nm.trim().toLowerCase() === "arena" ||
      nm.trim().toLowerCase() === "arena esportiva" ||
      nm.trim().toLowerCase() === "system" ||
      (Boolean(arenaId) && nm.trim() === `Arena ${arenaId}`) ||
      nm.toLowerCase().includes("atletahub mock") ||
      nm.toLowerCase().includes("arena sem nome");

    if (isPlaceholderName(name)) name = "";
    if (isPlaceholderEmail(email)) email = "";

    if (firestoreDb && arenaId && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      let resolvedOwnerUid = "";

      // 1. Tenta buscar no documento arenas/{arenaId}
      try {
        const arenaRes = await getFirestoreDoc(firestoreDb, "arenas", arenaId);
        if (arenaRes.exists && arenaRes.data) {
          const a = arenaRes.data || {};
          if (!name || isPlaceholderName(name)) name = a.name || a.arenaName || a.ownerName || a.legalName || "";
          if (!email || isPlaceholderEmail(email)) email = a.email || a.ownerEmail || a.contactEmail || "";
          if (!cpfCnpj) cpfCnpj = a.cpfCnpj || a.cnpj || a.cpf || a.document || "";
          if (!phone) phone = a.phone || a.whatsapp || a.phoneNumber || "";
          if (!customerId) customerId = a.asaasCustomerId || a.customerId || "";

          // Identifica ownerId/ownerUid canônico da arena
          const ownerUid = a.ownerId || a.userId || a.ownerUid || a.arenaOwnerId;
          if (ownerUid && typeof ownerUid === "string" && ownerUid.trim()) {
            resolvedOwnerUid = ownerUid.trim();
          }
        }
      } catch (e) {
        if (isPermissionDeniedError(e)) {
          console.warn("[SubscriptionService] Permissão negada ao buscar dados em arenas:", e);
        } else {
          console.warn("[SubscriptionService] Erro ao buscar dados em arenas:", e);
        }
      }

      // 2. Tenta buscar em arena_financial_configs/{arenaId}
      if (!name || !email || isPlaceholderName(name) || isPlaceholderEmail(email) || !customerId || !cpfCnpj || !phone || !resolvedOwnerUid) {
        try {
          const configRes = await getFirestoreDoc(firestoreDb, "arena_financial_configs", arenaId);
          if (configRes.exists && configRes.data) {
            const fc = configRes.data || {};
            if (!customerId) customerId = fc.asaasCustomerId || fc.customerId || "";
            if (!name || isPlaceholderName(name)) name = fc.ownerName || fc.arenaName || fc.name || "";
            if (!email || isPlaceholderEmail(email)) email = fc.ownerEmail || fc.email || "";
            if (!cpfCnpj) cpfCnpj = fc.ownerCpfCnpj || fc.cpfCnpj || fc.cnpj || fc.cpf || "";
            if (!phone) phone = fc.phone || fc.whatsapp || fc.ownerPhone || "";
            if (!resolvedOwnerUid) {
              const cfgOwner = fc.ownerId || fc.ownerUid || fc.userId;
              if (cfgOwner && typeof cfgOwner === "string" && cfgOwner.trim()) {
                resolvedOwnerUid = cfgOwner.trim();
              }
            }
          }
        } catch (e) {
          if (isPermissionDeniedError(e)) {
            console.warn("[SubscriptionService] Permissão negada ao buscar dados em arena_financial_configs:", e);
          } else {
            console.warn("[SubscriptionService] Erro ao buscar dados em arena_financial_configs:", e);
          }
        }
      }

      // 3. Tenta buscar em arena_public_configs/{arenaId}
      if (!name || !email || isPlaceholderName(name) || isPlaceholderEmail(email) || !customerId || !cpfCnpj || !phone || !resolvedOwnerUid) {
        try {
          const pubRes = await getFirestoreDoc(firestoreDb, "arena_public_configs", arenaId);
          if (pubRes.exists && pubRes.data) {
            const pc = pubRes.data || {};
            if (!name || isPlaceholderName(name)) name = pc.arenaName || pc.name || pc.ownerName || "";
            if (!email || isPlaceholderEmail(email)) email = pc.email || pc.ownerEmail || pc.contactEmail || "";
            if (!phone) phone = pc.phone || pc.whatsapp || "";
            if (!cpfCnpj) cpfCnpj = pc.cpfCnpj || pc.cnpj || pc.cpf || "";
            if (!customerId) customerId = pc.asaasCustomerId || pc.customerId || "";
            if (!resolvedOwnerUid) {
              const pubOwner = pc.ownerId || pc.ownerUid || pc.userId;
              if (pubOwner && typeof pubOwner === "string" && pubOwner.trim()) {
                resolvedOwnerUid = pubOwner.trim();
              }
            }
          }
        } catch (e) {
          if (isPermissionDeniedError(e)) {
            console.warn("[SubscriptionService] Permissão negada ao buscar dados em arena_public_configs:", e);
          } else {
            console.warn("[SubscriptionService] Erro ao buscar dados em arena_public_configs:", e);
          }
        }
      }

      // 4. Se tiver resolvedOwnerUid canônico, consulta o perfil do proprietário em users/{resolvedOwnerUid}
      if (resolvedOwnerUid && resolvedOwnerUid !== arenaId && (!name || !email || !cpfCnpj || !customerId || !phone || isPlaceholderName(name) || isPlaceholderEmail(email))) {
        try {
          const ownerUserRes = await getFirestoreDoc(firestoreDb, "users", resolvedOwnerUid);
          if (ownerUserRes.exists && ownerUserRes.data) {
            const ou = ownerUserRes.data || {};
            if (!name || isPlaceholderName(name)) name = ou.name || ou.displayName || ou.ownerName || "";
            if (!email || isPlaceholderEmail(email)) email = ou.email || ou.ownerEmail || "";
            if (!cpfCnpj) cpfCnpj = ou.cpf || ou.cnpj || ou.cpfCnpj || ou.document || "";
            if (!phone) phone = ou.phone || ou.phoneNumber || ou.whatsappContact || "";
            if (!customerId) customerId = ou.asaasCustomerId || ou.customerId || "";
          }
        } catch (err) {
          if (isPermissionDeniedError(err)) {
            console.warn("[SubscriptionService] Permissão negada ao buscar owner da arena no Firestore:", err);
          } else {
            console.warn("[SubscriptionService] Erro ao buscar owner da arena no Firestore:", err);
          }
        }
      }

      // 5. Se ainda faltar, busca em courts onde arenaId == arenaId
      if (!name || !email || isPlaceholderName(name) || isPlaceholderEmail(email) || !customerId) {
        try {
          const courts = await queryFirestoreDocs(firestoreDb, "courts", [{ field: "arenaId", op: "==", value: arenaId }], 1);
          if (courts.length > 0) {
            const c = courts[0];
            if (!name || isPlaceholderName(name)) name = c.ownerName || c.arenaName || "";
            if (!email || isPlaceholderEmail(email)) email = c.ownerEmail || "";
            if (!phone) phone = c.ownerPhone || c.phone || "";
            if (!cpfCnpj) cpfCnpj = c.ownerCpfCnpj || c.cpfCnpj || "";
            if (!customerId) customerId = c.asaasCustomerId || c.customerId || "";
          }
        } catch (e) {
          if (isPermissionDeniedError(e)) {
            console.warn("[SubscriptionService] Permissão negada ao buscar dados em courts:", e);
          } else {
            console.warn("[SubscriptionService] Erro ao buscar dados em courts:", e);
          }
        }
      }
    }

    const missingFields: string[] = [];
    if (!name || isPlaceholderName(name)) {
      name = "";
      missingFields.push("name");
    }
    if (!email || isPlaceholderEmail(email)) {
      email = "";
      missingFields.push("email");
    }

    const isValid = Boolean(
      name &&
      name.trim().length >= 2 &&
      email &&
      email.includes("@") &&
      email.includes(".") &&
      missingFields.length === 0
    );

    return {
      name: name.trim(),
      email: email.trim(),
      cpfCnpj: cpfCnpj ? cpfCnpj.trim() : undefined,
      phone: phone ? phone.trim() : undefined,
      customerId: customerId ? customerId.trim() : undefined,
      isValid,
      missingFields,
    };
  }

  /**
   * Alias flexível para resolução de cliente real
   */
  static async resolveRealCustomer(
    arenaId: string,
    requestHintOrDb?: any,
    firestoreDbOrHint?: any
  ) {
    if (requestHintOrDb && typeof requestHintOrDb === "object" && ("name" in requestHintOrDb || "email" in requestHintOrDb || "cpfCnpj" in requestHintOrDb)) {
      return this.resolveRealCustomerData(arenaId, firestoreDbOrHint, requestHintOrDb);
    }
    return this.resolveRealCustomerData(arenaId, requestHintOrDb, firestoreDbOrHint);
  }

  /**
   * Cria uma nova assinatura / mensalidade recorrente no gateway e persiste no sistema.
   * Não mascara falha real do gateway nem do Firestore.
   */
  static async createSubscription(
    request: CreateSubscriptionRequest,
    firestoreDb?: any
  ): Promise<SubscriptionResponse> {
    const gateway = this.getGateway();

    // Validação estrita de dados cadastrais: nunca inventar placeholders
    if (!request.customerId) {
      if (!request.customerName || !request.customerEmail || !request.customerEmail.includes("@") || request.customerName.trim().length < 2) {
        return {
          success: false,
          gateway: gateway.id || "asaas",
          failedStep: "CUSTOMER_VALIDATION",
          errorMessage: "Dados cadastrais incompletos da Arena/Cliente: nome e email válidos são obrigatórios para emissão de assinatura no gateway.",
        };
      }
    }

    // 1. Cria a assinatura no Gateway
    const gatewayRes = await gateway.createSubscription(request);
    if (!gatewayRes || !gatewayRes.success || !gatewayRes.subscriptionId) {
      return {
        success: false,
        gateway: gateway.id || "asaas",
        failedStep: gatewayRes?.failedStep || "ASAAS_SYNC",
        errorMessage: gatewayRes?.errorMessage || "Falha ao criar assinatura no gateway.",
      };
    }

    const subId = request.id || gatewayRes.subscriptionId;
    const nowIso = new Date().toISOString();

    const record: SubscriptionRecord = {
      id: subId,
      userId: request.userId,
      arenaId: request.arenaId || null,
      planId: request.planId,
      planName: request.planName || "Plano Mensal",
      gateway: gatewayRes.gateway || gateway.id,
      gatewaySubscriptionId: gatewayRes.gatewaySubscriptionId || subId,
      value: request.value,
      cycle: request.cycle || "MONTHLY",
      status: gatewayRes.status || "ACTIVE",
      startDate: nowIso,
      nextDueDate: gatewayRes.nextDueDate || request.nextDueDate || nowIso.split("T")[0],
      billingType: request.billingType || "PIX",
      description: request.description,
      split: request.split,
      createdAt: nowIso,
      updatedAt: nowIso,
      financialModel: request.financialModel || (request.planName?.toLowerCase().includes("hibrid") ? "HYBRID" : "FIXED_MONTHLY"),
      productType: request.productType || (request.financialModel === "OPERATION_ONLY" || request.planId === "plan-operation-only" ? "OPERATION_ONLY" : "COURT_MODEL"),
      customerId: request.customerId,
      customerName: request.customerName,
      customerEmail: request.customerEmail,
      customerCpfCnpj: request.customerCpfCnpj,
    };

    // 2. Armazena em memória
    inMemorySubscriptions.set(subId, record);

    // 3. Persiste no Firestore se disponível (MANDATÓRIO: nunca declarar sucesso se o Firestore falhar)
    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(firestoreDb, "subscriptions", subId, record, true);
      } catch (err: any) {
        console.error("[SubscriptionService] Erro crítico ao persistir assinatura no Firestore:", err);
        return {
          success: false,
          subscriptionId: subId,
          gateway: gatewayRes.gateway || gateway.id || "asaas",
          failedStep: "FIRESTORE_PERSISTENCE",
          errorMessage: `Falha na persistência da assinatura no Firestore: ${err?.message || String(err)}`,
        };
      }
    }

    return {
      ...gatewayRes,
      subscriptionId: subId,
    };
  }

  /**
   * Consulta uma assinatura por ID (do banco ou gateway).
   */
  static async getSubscription(
    subscriptionId: string,
    firestoreDb?: any
  ): Promise<{ success: boolean; subscription?: SubscriptionRecord; errorMessage?: string }> {
    // 1. Tenta Firestore
    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const snap = await getFirestoreDoc(firestoreDb, "subscriptions", subscriptionId);
        if (snap.exists && snap.data) {
          return { success: true, subscription: snap.data as SubscriptionRecord };
        }
      } catch (e) {
        if (isPermissionDeniedError(e)) {
          console.warn("[SubscriptionService] Permissão negada ao buscar assinatura no Firestore:", e);
        } else {
          console.warn("[SubscriptionService] Erro ao buscar no Firestore:", e);
        }
      }
    }

    // 2. Tenta Memória
    if (inMemorySubscriptions.has(subscriptionId)) {
      return { success: true, subscription: inMemorySubscriptions.get(subscriptionId)! };
    }
    for (const sub of inMemorySubscriptions.values()) {
      if (sub.arenaId === subscriptionId) {
        return { success: true, subscription: sub };
      }
    }

    // 3. Tenta Gateway
    const gateway = this.getGateway();
    return gateway.getSubscription(subscriptionId);
  }

  /**
   * Atualiza os dados de uma assinatura existente no gateway e no sistema.
   */
  static async updateSubscription(
    request: UpdateSubscriptionRequest,
    firestoreDb?: any
  ): Promise<SubscriptionResponse> {
    const gateway = this.getGateway();
    if (!gateway.updateSubscription) {
      return {
        success: false,
        gateway: gateway.id,
        failedStep: "GATEWAY_UNSUPPORTED",
        errorMessage: `Gateway ${gateway.id} não suporta atualização de assinaturas.`,
      };
    }

    const result = await gateway.updateSubscription(request);
    if (!result || !result.success) {
      return {
        success: false,
        gateway: gateway.id,
        failedStep: result?.failedStep || "ASAAS_SYNC",
        errorMessage: result?.errorMessage || "Falha ao atualizar assinatura no gateway.",
      };
    }

    const nowIso = new Date().toISOString();
    const existing = inMemorySubscriptions.get(request.subscriptionId);
    if (existing) {
      if (request.value !== undefined) existing.value = request.value;
      if (request.cycle) existing.cycle = request.cycle;
      if (result.nextDueDate) existing.nextDueDate = result.nextDueDate;
      if (result.status) existing.status = result.status;
      existing.updatedAt = nowIso;
      inMemorySubscriptions.set(request.subscriptionId, existing);
    }

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const updatePayload: any = { updatedAt: nowIso };
        if (request.value !== undefined) updatePayload.value = request.value;
        if (request.cycle) updatePayload.cycle = request.cycle;
        if (result.nextDueDate) updatePayload.nextDueDate = result.nextDueDate;
        if (result.status) updatePayload.status = result.status;
        await setFirestoreDoc(firestoreDb, "subscriptions", request.subscriptionId, updatePayload, true);
      } catch (e: any) {
        console.error("[SubscriptionService] Erro crítico ao atualizar assinatura no Firestore:", e);
        return {
          success: false,
          gateway: gateway.id,
          failedStep: "FIRESTORE_PERSISTENCE",
          errorMessage: `Falha na persistência da atualização da assinatura no Firestore: ${e?.message || String(e)}`,
        };
      }
    }

    return result;
  }

  /**
   * Cancela uma assinatura recorrente no gateway e persiste no sistema.
   */
  static async cancelSubscription(
    subscriptionId: string,
    reason?: string,
    firestoreDb?: any
  ): Promise<CancelSubscriptionResponse> {
    const gateway = this.getGateway();
    const result = await gateway.cancelSubscription(subscriptionId, reason);

    if (!result || !result.success) {
      return {
        success: false,
        subscriptionId,
        status: "ACTIVE",
        cancellationDate: "",
        failedStep: result?.failedStep || "ASAAS_SYNC",
        errorMessage: result?.errorMessage || "Falha ao cancelar assinatura no gateway.",
      };
    }

    const nowIso = new Date().toISOString();
    const existing = inMemorySubscriptions.get(subscriptionId);
    if (existing) {
      existing.status = "CANCELLED";
      existing.cancellationDate = nowIso;
      existing.cancellationReason = reason || "Cancelado";
      existing.updatedAt = nowIso;
      inMemorySubscriptions.set(subscriptionId, existing);
    }

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "subscriptions",
          subscriptionId,
          {
            status: "CANCELLED",
            cancellationDate: nowIso,
            cancellationReason: reason || "Cancelado",
            updatedAt: nowIso,
          },
          true
        );
      } catch (e: any) {
        console.error("[SubscriptionService] Erro crítico ao atualizar cancelamento no Firestore:", e);
        return {
          success: false,
          subscriptionId,
          status: "ACTIVE",
          cancellationDate: "",
          failedStep: "FIRESTORE_PERSISTENCE",
          errorMessage: `Falha na persistência do cancelamento no Firestore: ${e?.message || String(e)}`,
        };
      }
    }

    return result;
  }

  /**
   * Determina o status da assinatura considerando a carência comercial de 3 DIAS (D+1 a D+3) pós-vencimento:
   * - Até a data do vencimento (D+0): ACTIVE
   * - De D+1 a D+3 pós-vencimento: GRACE_PERIOD (em carência de 3 dias, opera normalmente sob o plano pago)
   * - A partir de D+4 pós-vencimento: SUSPENDED (Carência expirada, bloqueia visibilidade/operação)
   */
  static calculateGracePeriodStatus(
    dueDateStr?: string | null,
    now: Date = new Date()
  ): {
    state: "ACTIVE" | "GRACE_PERIOD" | "SUSPENDED";
    daysOverdue: number;
    hoursOverdue: number;
    isGracePeriod: boolean;
  } {
    if (!dueDateStr) {
      return { state: "ACTIVE", daysOverdue: 0, hoursOverdue: 0, isGracePeriod: false };
    }

    const cleanDate = String(dueDateStr).split("T")[0];
    const dueParts = cleanDate.split("-").map(Number);
    if (dueParts.length !== 3 || dueParts.some(isNaN)) {
      return { state: "ACTIVE", daysOverdue: 0, hoursOverdue: 0, isGracePeriod: false };
    }

    const dueDay = new Date(Date.UTC(dueParts[0], dueParts[1] - 1, dueParts[2], 0, 0, 0, 0));
    const currentDayStr = now.toISOString().split("T")[0];
    const currParts = currentDayStr.split("-").map(Number);
    const currentDay = new Date(Date.UTC(currParts[0], currParts[1] - 1, currParts[2], 0, 0, 0, 0));

    const diffMs = currentDay.getTime() - dueDay.getTime();
    const daysOverdue = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let rawDueMs = dueDay.getTime();
    if (dueDateStr.includes("T")) {
      const parsed = new Date(dueDateStr).getTime();
      if (!isNaN(parsed)) rawDueMs = parsed;
    }
    const rawDiffMs = now.getTime() - rawDueMs;
    const hoursOverdue = rawDiffMs > 0 ? Math.floor(rawDiffMs / (1000 * 60 * 60)) : 0;

    if (daysOverdue <= 0) {
      return { state: "ACTIVE", daysOverdue: Math.min(0, daysOverdue), hoursOverdue: 0, isGracePeriod: false };
    } else if (daysOverdue <= 3) {
      return { state: "GRACE_PERIOD", daysOverdue, hoursOverdue, isGracePeriod: true };
    } else {
      return { state: "SUSPENDED", daysOverdue, hoursOverdue, isGracePeriod: false };
    }
  }

  /**
   * Registra ou atualiza um pagamento de mensalidade pertencente a uma assinatura.
   */
  static async recordSubscriptionPayment(
    payment: SubscriptionPaymentRecord,
    firestoreDb?: any
  ): Promise<void> {
    const subId = payment.subscriptionId;

    // Memória
    const currentList = inMemorySubscriptionPayments.get(subId) || [];
    const index = currentList.findIndex((p) => p.id === payment.id || p.gatewayPaymentId === payment.gatewayPaymentId);
    if (index >= 0) {
      currentList[index] = { ...currentList[index], ...payment, updatedAt: new Date().toISOString() };
    } else {
      currentList.push(payment);
    }
    inMemorySubscriptionPayments.set(subId, currentList);

    // Determina o novo status da assinatura baseado no pagamento e carência
    let newSubStatus: string | null = null;
    let newCourtStatus: string | null = null;

    if (payment.status === "RECEIVED" || payment.status === "CONFIRMED") {
      newSubStatus = "ACTIVE";
      newCourtStatus = "em_dia";
    } else if (payment.status === "OVERDUE") {
      const grace = this.calculateGracePeriodStatus(payment.dueDate);
      if (grace.state === "SUSPENDED") {
        newSubStatus = "SUSPENDED";
        newCourtStatus = "inadimplente";
      } else {
        newSubStatus = "OVERDUE";
        newCourtStatus = "pendente_5dias";
      }
    }

    // Atualiza assinatura em memória
    let existingSub = inMemorySubscriptions.get(subId);
    if (!existingSub && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const subRes = await getFirestoreDoc(firestoreDb, "subscriptions", subId);
        if (subRes.exists && subRes.data) {
          existingSub = subRes.data as SubscriptionRecord;
          inMemorySubscriptions.set(subId, existingSub);
        }
      } catch (e) {
        if (isPermissionDeniedError(e)) {
          console.warn("[SubscriptionService] Permissão negada ao buscar assinatura no Firestore:", e);
        } else {
          console.warn("[SubscriptionService] Erro ao buscar assinatura no Firestore:", e);
        }
      }
    }

    if (!existingSub) {
      throw new Error(`Erro de integridade: Assinatura pai ${subId} não foi localizada para registrar o pagamento.`);
    }

    if (newSubStatus) {
      existingSub.status = newSubStatus as any;
      if (newSubStatus === "ACTIVE") {
        existingSub.lastPaymentDate = payment.paymentDate || new Date().toISOString();
      }
      existingSub.updatedAt = new Date().toISOString();
    }
    inMemorySubscriptions.set(subId, existingSub);

    // Firestore
    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "subscription_payments",
          payment.gatewayPaymentId || payment.id,
          payment,
          true
        );

        // Recupera a assinatura pai para obter arenaId
        let arenaId: string | null = null;
        try {
          const subRes = await getFirestoreDoc(firestoreDb, "subscriptions", subId);
          if (subRes.exists && subRes.data) {
            arenaId = subRes.data?.arenaId || null;
          }
        } catch (e) {
          console.warn("[SubscriptionService] Erro ao buscar assinatura pai:", e);
        }

        // Atualiza a assinatura pai
        if (newSubStatus) {
          const subUpdate: any = {
            status: newSubStatus,
            updatedAt: new Date().toISOString(),
          };
          if (newSubStatus === "ACTIVE") {
            subUpdate.lastPaymentDate = payment.paymentDate || new Date().toISOString();
          }
          await setFirestoreDoc(firestoreDb, "subscriptions", subId, subUpdate, true);
          
          // Sincroniza o status financeiro de todas as quadras da arena (Status Autoritativo)
          if (arenaId) {
            try {
              const courts = await queryFirestoreDocs(firestoreDb, "courts", [{ field: "arenaId", op: "==", value: arenaId }]);
              if (courts.length > 0) {
                if (isV8Db(firestoreDb)) {
                  const batch = firestoreDb.batch();
                  courts.forEach((c: any) => {
                    const updatePayload: any = { paymentStatus: "em_dia", updatedAt: new Date().toISOString() };
                    if (c.verificationStatus === "SUSPENDED") {
                      updatePayload.verificationStatus = "VERIFIED";
                    }
                    batch.update(c.ref, updatePayload);
                  });
                  await batch.commit();
                } else {
                  const modularDb = resolveModularDb(firestoreDb);
                  if (modularDb) {
                    const batch = writeBatch(modularDb);
                    courts.forEach((c: any) => {
                      const updatePayload: any = { paymentStatus: "em_dia", updatedAt: new Date().toISOString() };
                      if (c.verificationStatus === "SUSPENDED") {
                        updatePayload.verificationStatus = "VERIFIED";
                      }
                      const docRef = doc(modularDb, "courts", c.id);
                      batch.update(docRef, updatePayload);
                    });
                    await batch.commit();
                  }
                }
                console.log(`[SubscriptionService] Atualizou status de ${courts.length} quadras da arena ${arenaId} para ${newCourtStatus}`);
              }
            } catch (e) {
              console.warn(`[SubscriptionService] Erro ao sincronizar status nas quadras da arena ${arenaId}:`, e);
            }
          }
        }
      } catch (err: any) {
        console.error("[SubscriptionService] Erro ao gravar pagamento de assinatura no Firestore:", err);
        throw err;
      }
    }
  }

  /**
   * Obtém o registro de assinatura da arena por seu arenaId canônico.
   */
  static async getSubscriptionByArenaId(
    arenaId: string,
    firestoreDb?: any
  ): Promise<SubscriptionRecord | null> {
    if (!arenaId || arenaId === "arena_padrao" || arenaId === "minha_arena") {
      return null;
    }

    for (const sub of inMemorySubscriptions.values()) {
      if (sub.arenaId === arenaId || sub.userId === arenaId) {
        return sub;
      }
    }

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const docs = await queryFirestoreDocs(
          firestoreDb,
          "subscriptions",
          [{ field: "arenaId", op: "==", value: arenaId }],
          1
        );
        if (docs.length > 0) {
          return docs[0] as SubscriptionRecord;
        }
        // Ausência de assinatura é estado normal para modelos sem mensalidade fixa (SPLIT) ou novas arenas
        return null;
      } catch (err: any) {
        if (isPermissionDeniedError(err)) {
          console.warn("[SubscriptionService] Permissão negada ao consultar assinatura no Firestore (tratado de forma resiliente):", err?.message || err);
          return null;
        }
        console.warn("[SubscriptionService] Erro ao buscar assinatura no Firestore:", err);
        throw err;
      }
    }

    return null;
  }

  /**
   * Sincroniza/atualiza o contrato de assinatura da Arena no gateway real (Asaas) quando há troca de plano.
   * Não altera Firestore nem estado em memória sem confirmação de sucesso do gateway.
   * Nunca declara sucesso se a persistência no Firestore falhar.
   */
  static async migrateSubscriptionPlan(
    arenaId: string,
    newPlanId: string,
    newPlanName: string,
    newValue: number,
    financialModel: string,
    firestoreDb?: any,
    customerHint?: { name?: string; email?: string; cpfCnpj?: string; phone?: string; customerId?: string }
  ): Promise<{ success: boolean; message: string; subscriptionId?: string; failedStep?: string }> {
    const nowIso = new Date().toISOString();

    // 1. Localiza assinatura existente da arena em memória ou Firestore
    let existingSub: SubscriptionRecord | null = null;
    for (const sub of inMemorySubscriptions.values()) {
      if ((sub.arenaId === arenaId || sub.userId === arenaId) && sub.status !== "CANCELLED" && sub.status !== "INACTIVE") {
        existingSub = sub;
        break;
      }
    }

    if (!existingSub && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const docs = await queryFirestoreDocs(
          firestoreDb,
          "subscriptions",
          [{ field: "arenaId", op: "==", value: arenaId }]
        );
        if (docs.length > 0) {
          const activeDocs = docs
            .map((d: any) => d as SubscriptionRecord)
            .filter((s: SubscriptionRecord) => s && s.status !== "CANCELLED" && s.status !== "INACTIVE");
          if (activeDocs.length > 0) {
            existingSub = activeDocs[0];
          }
        }
      } catch (err: any) {
        if (isPermissionDeniedError(err)) {
          console.warn("[SubscriptionService] Permissão negada ao buscar assinatura existente para migração:", err?.message || err);
        } else {
          console.warn("[SubscriptionService] Erro ao buscar assinatura existente para migração:", err);
        }
      }
    }

    // Se o novo modelo for SPLIT (sem mensalidade):
    if (financialModel === "SPLIT" || (newValue <= 0 && financialModel !== "FIXED_MONTHLY" && financialModel !== "HYBRID" && financialModel !== "OPERATION_ONLY")) {
      if (existingSub) {
        const cancelRes = await this.cancelSubscription(
          existingSub.id,
          "Migração comercial para modelo SPLIT (sem mensalidade fixa)",
          firestoreDb
        );
        if (!cancelRes.success) {
          return {
            success: false,
            failedStep: cancelRes.failedStep || "ASAAS_SYNC",
            message: `Falha ao cancelar assinatura existente no gateway: ${cancelRes.errorMessage || "Erro desconhecido"}`,
          };
        }
      }
      return { success: true, message: "Plano SPLIT ativado e assinatura recorrente cancelada no gateway." };
    }

    // Se o modelo tiver mensalidade (HYBRID ou FIXED_MONTHLY):
    if (existingSub) {
      // Atualiza a assinatura REAL no gateway Asaas
      const updateRes = await this.updateSubscription(
        {
          subscriptionId: existingSub.id,
          gatewaySubscriptionId: existingSub.gatewaySubscriptionId || existingSub.id,
          value: newValue,
          updatePendingPayments: true,
        },
        firestoreDb
      );

      if (!updateRes || !updateRes.success) {
        return {
          success: false,
          failedStep: updateRes?.failedStep || "ASAAS_SYNC",
          message: `Falha ao atualizar assinatura no gateway: ${updateRes?.errorMessage || "Erro no gateway"}`,
        };
      }

      // Gateway confirmou sucesso! Sincroniza metadados do plano
      existingSub.planId = newPlanId;
      existingSub.planName = newPlanName;
      existingSub.value = newValue;
      if (financialModel) {
        existingSub.financialModel = financialModel as any;
        existingSub.productType = financialModel === "OPERATION_ONLY" ? "OPERATION_ONLY" : "COURT_MODEL";
      }
      existingSub.updatedAt = nowIso;
      inMemorySubscriptions.set(existingSub.id, existingSub);

      if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          await setFirestoreDoc(
            firestoreDb,
            "subscriptions",
            existingSub.id,
            {
              planId: newPlanId,
              planName: newPlanName,
              value: newValue,
              financialModel: (financialModel as any) || existingSub.financialModel,
              productType: financialModel === "OPERATION_ONLY" ? "OPERATION_ONLY" : "COURT_MODEL",
              updatedAt: nowIso,
            },
            true
          );
        } catch (e: any) {
          console.error("[SubscriptionService] Erro crítico ao sincronizar metadados da assinatura no Firestore:", e);
          return {
            success: false,
            failedStep: "FIRESTORE_PERSISTENCE",
            message: `Falha na persistência da migração no Firestore: ${e?.message || String(e)}`,
          };
        }
      }

      return {
        success: true,
        message: `Assinatura ${existingSub.id} atualizada com sucesso no gateway para ${newPlanName} (R$ ${newValue}).`,
        subscriptionId: existingSub.id,
      };
    } else {
      // 2. Busca e valida dados cadastrais reais da Arena/Proprietário
      const realCustomer = await this.resolveRealCustomerData(arenaId, firestoreDb, customerHint);

      if (!realCustomer.isValid) {
        return {
          success: false,
          failedStep: "CUSTOMER_VALIDATION",
          message: "Dados cadastrais incompletos da Arena/Proprietário: nome e e-mail válidos são obrigatórios para emissão de assinatura no gateway.",
        };
      }

      // Cria uma nova assinatura REAL no gateway com dados cadastrais autênticos
      const subRes = await this.createSubscription(
        {
          id: `sub_${arenaId}_${Date.now()}`,
          userId: arenaId,
          arenaId,
          customerId: realCustomer.customerId,
          customerName: realCustomer.name,
          customerEmail: realCustomer.email,
          customerCpfCnpj: realCustomer.cpfCnpj,
          planId: newPlanId,
          planName: newPlanName,
          value: newValue,
          cycle: "MONTHLY",
          financialModel: (financialModel as any) || "FIXED_MONTHLY",
          productType: financialModel === "OPERATION_ONLY" ? "OPERATION_ONLY" : "COURT_MODEL",
          nextDueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        },
        firestoreDb
      );

      if (!subRes || !subRes.success || !subRes.subscriptionId) {
        return {
          success: false,
          failedStep: subRes?.failedStep || "ASAAS_SYNC",
          message: `Falha ao criar nova assinatura no gateway: ${subRes?.errorMessage || "Erro no gateway"}`,
        };
      }

      return {
        success: true,
        message: `Nova assinatura criada com sucesso no gateway para ${newPlanName} (R$ ${newValue}).`,
        subscriptionId: subRes.subscriptionId,
      };
    }
  }

  /**
   * Lista os pagamentos efetuados ou pendentes de uma assinatura.
   */
  static async listSubscriptionPayments(
    subscriptionId: string,
    firestoreDb?: any
  ): Promise<ListSubscriptionPaymentsResponse> {
    // 1. Tenta Firestore
    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const docs = await queryFirestoreDocs(
          firestoreDb,
          "subscription_payments",
          [{ field: "subscriptionId", op: "==", value: subscriptionId }]
        );
        if (docs.length > 0) {
          const payments = docs as SubscriptionPaymentRecord[];
          return {
            success: true,
            subscriptionId,
            payments,
            totalCount: payments.length,
          };
        }
      } catch (err) {
        if (isPermissionDeniedError(err)) {
          console.warn("[SubscriptionService] Permissão negada ao listar pagamentos do Firestore:", err);
        } else {
          console.warn("[SubscriptionService] Erro ao listar pagamentos do Firestore:", err);
        }
      }
    }

    // 2. Memória
    if (inMemorySubscriptionPayments.has(subscriptionId)) {
      const payments = inMemorySubscriptionPayments.get(subscriptionId)!;
      return {
        success: true,
        subscriptionId,
        payments,
        totalCount: payments.length,
      };
    }

    // 3. Gateway
    const gateway = this.getGateway();
    if (gateway.listSubscriptionPayments) {
      return gateway.listSubscriptionPayments(subscriptionId);
    }

    return {
      success: true,
      subscriptionId,
      payments: [],
      totalCount: 0,
    };
  }

  /**
   * Processa eventos de webhook específicos de assinaturas e mensalidades.
   */
  static async processSubscriptionWebhookEvent(
    payload: any,
    firestoreDb?: any
  ): Promise<{ success: boolean; event: string; status?: string; message?: string; failedStep?: string; renewal?: any }> {
    const event = String(payload?.event || "").toUpperCase();
    let subId: string | null = null;
    if (typeof payload?.payment?.subscription === "string") {
      subId = payload.payment.subscription;
    } else if (payload?.payment?.subscription?.id) {
      subId = payload.payment.subscription.id;
    } else if (typeof payload?.subscription === "string") {
      subId = payload.subscription;
    } else if (payload?.subscription?.id) {
      subId = payload.subscription.id;
    } else if (payload?.subscriptionId) {
      subId = payload.subscriptionId;
    } else if (payload?.payment?.subscriptionId) {
      subId = payload.payment.subscriptionId;
    }

    if (!subId) {
      const extRef = String(payload?.payment?.externalReference || payload?.externalReference || "").trim();
      const payId = String(payload?.payment?.id || payload?.id || "").trim();
      if (extRef) {
        for (const sub of inMemorySubscriptions.values()) {
          if (sub.id === extRef || sub.arenaId === extRef || sub.gatewaySubscriptionId === extRef) {
            subId = sub.id;
            break;
          }
        }
      }
      if (!subId && payId) {
        for (const [sId, payments] of inMemorySubscriptionPayments.entries()) {
          if (payments.some((p) => p.id === payId || p.gatewayPaymentId === payId)) {
            subId = sId;
            break;
          }
        }
      }
    }

    if (!subId) {
      return { success: false, event, message: "ID da assinatura não encontrado no payload" };
    }

    const subscriptionData =
      typeof payload?.subscription === "object"
        ? payload.subscription
        : typeof payload?.payment?.subscription === "object"
        ? payload.payment.subscription
        : payload;

    const nowIso = new Date().toISOString();

    if (event === "SUBSCRIPTION_CREATED") {
      const record: Partial<SubscriptionRecord> = {
        id: subId,
        gatewaySubscriptionId: subId,
        status: "ACTIVE",
        cycle: subscriptionData?.cycle || "MONTHLY",
        value: Number(subscriptionData?.value || 0),
        nextDueDate: subscriptionData?.nextDueDate || "",
        updatedAt: nowIso,
      };
      if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          await setFirestoreDoc(firestoreDb, "subscriptions", subId, record, true);
        } catch (err: any) {
          console.error("[SubscriptionService] Erro crítico ao persistir SUBSCRIPTION_CREATED no Firestore:", err);
          return {
            success: false,
            event,
            failedStep: "FIRESTORE_PERSISTENCE",
            message: `Falha na persistência do evento SUBSCRIPTION_CREATED no Firestore: ${err?.message || String(err)}`,
          };
        }
      }
      inMemorySubscriptions.set(subId, record as any);
      return { success: true, event, status: "ACTIVE", message: `Assinatura ${subId} criada` };
    }

    if (event === "SUBSCRIPTION_UPDATED") {
      const updateData: Partial<SubscriptionRecord> = {
        value: subscriptionData?.value !== undefined ? Number(subscriptionData.value) : undefined,
        nextDueDate: subscriptionData?.nextDueDate,
        cycle: subscriptionData?.cycle,
        status: (subscriptionData?.status || "ACTIVE").toUpperCase() as any,
        updatedAt: nowIso,
      };
      if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          await setFirestoreDoc(firestoreDb, "subscriptions", subId, updateData, true);
        } catch (err: any) {
          console.error("[SubscriptionService] Erro crítico ao persistir SUBSCRIPTION_UPDATED no Firestore:", err);
          return {
            success: false,
            event,
            failedStep: "FIRESTORE_PERSISTENCE",
            message: `Falha na persistência do evento SUBSCRIPTION_UPDATED no Firestore: ${err?.message || String(err)}`,
          };
        }
      }
      const existing = inMemorySubscriptions.get(subId);
      if (existing) {
        inMemorySubscriptions.set(subId, { ...existing, ...updateData });
      }
      return { success: true, event, status: updateData.status, message: `Assinatura ${subId} atualizada` };
    }

    if (event === "SUBSCRIPTION_DELETED" || event === "SUBSCRIPTION_CANCELLED") {
      const cancelData = {
        status: "CANCELLED",
        cancellationDate: nowIso,
        updatedAt: nowIso,
      };
      if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          await setFirestoreDoc(firestoreDb, "subscriptions", subId, cancelData, true);
        } catch (err: any) {
          console.error("[SubscriptionService] Erro crítico ao persistir cancelamento da assinatura no Firestore:", err);
          return {
            success: false,
            event,
            failedStep: "FIRESTORE_PERSISTENCE",
            message: `Falha na persistência do cancelamento da assinatura no Firestore: ${err?.message || String(err)}`,
          };
        }
      }
      const existing = inMemorySubscriptions.get(subId);
      if (existing) {
        inMemorySubscriptions.set(subId, { ...existing, ...cancelData, status: "CANCELLED" as any });
      }
      return { success: true, event, status: "CANCELLED", message: `Assinatura ${subId} cancelada` };
    }

    // Pagamentos vinculados à assinatura
    if (event.startsWith("PAYMENT_") && subId) {
      const pay = payload.payment || payload;
      let payStatus: any = "PENDING";
      if (event === "PAYMENT_RECEIVED" || event === "PAYMENT_CONFIRMED") payStatus = "RECEIVED";
      else if (event === "PAYMENT_OVERDUE") payStatus = "OVERDUE";
      else if (event === "PAYMENT_REFUNDED") payStatus = "REFUNDED";
      else if (event === "PAYMENT_DELETED") payStatus = "CANCELLED";
      else if (event === "PAYMENT_FAILED" || event === "PAYMENT_REFUSED") payStatus = "REFUSED";

      const webhookKey = payload?.id || `${event}_${pay.id || pay.paymentId}_${payStatus}`;
      if (processedWebhookEventIds.has(webhookKey)) {
        return {
          success: true,
          event,
          status: payStatus,
          message: `Evento ${webhookKey} já processado anteriormente (idempotente).`,
        };
      }
      processedWebhookEventIds.add(webhookKey);

      const paymentRec: SubscriptionPaymentRecord = {
        id: pay.id || pay.paymentId || `pay_${Date.now()}`,
        subscriptionId: subId,
        gateway: "asaas",
        gatewayPaymentId: pay.id || pay.paymentId || `pay_${Date.now()}`,
        gatewaySubscriptionId: subId,
        value: Number(pay.value || pay.netValue || 0),
        dueDate: pay.dueDate || nowIso.split("T")[0],
        paymentDate: pay.paymentDate || (payStatus === "RECEIVED" ? nowIso : null),
        status: payStatus,
        billingType: pay.billingType || "PIX",
        invoiceUrl: pay.invoiceUrl,
        bankSlipUrl: pay.bankSlipUrl,
        createdAt: pay.dateCreated || nowIso,
        updatedAt: nowIso,
      };

      try {
        await this.recordSubscriptionPayment(paymentRec, firestoreDb);
      } catch (err: any) {
        console.error("[SubscriptionService] Erro ao gravar pagamento de assinatura via webhook:", err);
        return {
          success: false,
          event,
          failedStep: "FIRESTORE_PERSISTENCE",
          message: `Falha na persistência do pagamento da assinatura no Firestore: ${err?.message || String(err)}`,
        };
      }

      // BLOCO 10: Se o pagamento for confirmado/recebido, renova o ciclo operacional por mais 30 dias
      let renewalResult: any = null;
      if (payStatus === "RECEIVED") {
        try {
          renewalResult = await this.renewSubscriptionCycle(subId, {
            paymentId: pay.id || pay.paymentId,
            paymentReference: pay.externalReference,
            paidAmount: Number(pay.value || pay.netValue || 0),
            firestoreDb,
          });
        } catch (renewErr) {
          console.warn("[SubscriptionService] Erro ao renovar ciclo após pagamento confirmado via webhook:", renewErr);
        }
      }

      return {
        success: true,
        event,
        status: payStatus,
        renewal: renewalResult,
        message: `Pagamento ${paymentRec.id} de assinatura ${subId} processado como ${payStatus}`,
      };
    }

    return { success: true, event, message: `Evento ${event} registrado com sucesso.` };
  }

  /**
   * BLOCO 12K: Verifica se um pagamento ou externalReference pertence a uma assinatura ou mensalidade.
   */
  static isSubscriptionPayment(paymentId?: string, externalReference?: string): boolean {
    if (!paymentId && !externalReference) return false;
    const cleanExtRef = String(externalReference || "").trim();
    const cleanPayId = String(paymentId || "").trim();

    if (
      cleanExtRef.startsWith("sub_") ||
      cleanExtRef.startsWith("regularize_") ||
      cleanExtRef.startsWith("monthly_") ||
      cleanExtRef.startsWith("subscription_") ||
      cleanExtRef.startsWith("fixed_monthly_") ||
      cleanExtRef.startsWith("hybrid_") ||
      cleanExtRef.startsWith("plan_fixed_") ||
      cleanExtRef.startsWith("plan_hybrid_") ||
      cleanExtRef.startsWith("plan_")
    ) {
      return true;
    }

    if (cleanPayId) {
      for (const payments of inMemorySubscriptionPayments.values()) {
        if (payments.some((p) => p.id === cleanPayId || p.gatewayPaymentId === cleanPayId)) {
          return true;
        }
      }
    }

    if (cleanExtRef) {
      for (const sub of inMemorySubscriptions.values()) {
        if (sub.id === cleanExtRef || sub.arenaId === cleanExtRef || sub.gatewaySubscriptionId === cleanExtRef) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * BLOCO 12K: Verifica se um pagamento ou externalReference pertence ao Plano PDV / Vendas Locais.
   */
  static isPDVPayment(paymentId?: string, externalReference?: string): boolean {
    if (!paymentId && !externalReference) return false;
    const cleanExtRef = String(externalReference || "").trim().toLowerCase();
    const cleanPayId = String(paymentId || "").trim().toLowerCase();

    if (
      cleanExtRef.startsWith("pdv_") ||
      cleanExtRef.startsWith("plan_pdv_") ||
      cleanExtRef === "plan_pdv_vendas_locais" ||
      cleanExtRef.includes("pdv_vendas_locais")
    ) {
      return true;
    }

    if (cleanPayId && inMemoryConfirmedPDVPayments.has(cleanPayId)) {
      return true;
    }

    return false;
  }

  /**
   * BLOCO 12K: Confirmação de pagamento do Plano PDV / Vendas Locais.
   * - Confirma pagamento;
   * - Atualiza o ciclo do PDV (+30 dias de trial/período);
   * - Libera novamente os módulos correspondentes (PDV_ACTIVE);
   * - Não transforma PDV em FIXED_MONTHLY e não altera modelo da Arena;
   * - Usa o valor configurado no Bloco 12H (padrão R$ 89.90);
   * - Idempotente contra webhooks duplicados.
   */
  static async confirmPDVPayment(params: {
    paymentId?: string;
    externalReference?: string;
    arenaId?: string;
    paidAmount?: number;
    user?: any;
    firestoreDb?: any;
    now?: Date | string;
  }): Promise<{
    success: boolean;
    confirmed: boolean;
    alreadyConfirmed?: boolean;
    arenaId: string;
    commercialStatus: string;
    daysRemaining: number;
    newTrialEndAt: string;
    paidAmount: number;
    message: string;
  }> {
    const { paymentId, externalReference, arenaId, paidAmount, firestoreDb } = params;
    const nowTime = params.now ? (typeof params.now === "string" ? new Date(params.now).getTime() : params.now.getTime()) : Date.now();
    const nowIso = new Date(nowTime).toISOString();

    // 1. Identifica a arena alvo
    let targetArenaId = arenaId || "";
    if (!targetArenaId && externalReference) {
      if (externalReference.startsWith("pdv_")) {
        const parts = externalReference.split("_");
        if (parts.length >= 2) targetArenaId = parts[1];
      } else if (externalReference.startsWith("plan_pdv_")) {
        const parts = externalReference.split("_");
        if (parts.length >= 3) targetArenaId = parts[2];
      }
    }

    if (!targetArenaId) {
      targetArenaId = "arena_default";
    }

    const payKey = paymentId || externalReference || targetArenaId;

    // 2. IDEMPOTÊNCIA: Se já foi confirmado anteriormente para este pagamento
    const pdvPlan = CommercialPlanService.getPDVPlan();
    const expectedAmount = pdvPlan.monthlyFee ?? pdvPlan.monthlyAmount ?? 0;

    if (paymentId && inMemoryConfirmedPDVPayments.has(paymentId)) {
      const existing = inMemoryConfirmedPDVPayments.get(paymentId);
      return {
        success: true,
        confirmed: true,
        alreadyConfirmed: true,
        arenaId: existing.arenaId || targetArenaId,
        commercialStatus: "PDV_ACTIVE",
        daysRemaining: 30,
        newTrialEndAt: existing.newTrialEndAt || nowIso,
        paidAmount: existing.paidAmount || expectedAmount,
        message: "Pagamento do Plano PDV já havia sido confirmado anteriormente (idempotente).",
      };
    }

    // 3. Validação do valor em relação ao preço configurado do PDV (Bloco 12H)
    if (paidAmount !== undefined && paidAmount > 0 && paidAmount < expectedAmount - 0.01) {
      return {
        success: false,
        confirmed: false,
        arenaId: targetArenaId,
        commercialStatus: "PDV_SUSPENDED",
        daysRemaining: 0,
        newTrialEndAt: nowIso,
        paidAmount: paidAmount,
        message: `Valor pago (R$ ${paidAmount}) é inferior ao valor configurado para o Plano PDV (R$ ${expectedAmount}). Liberação não autorizada.`,
      };
    }

    // 4. Calcula extensão de 30 dias a partir do momento atual ou da data final existente
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    let currentEndTimestamp = nowTime;

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const arenaDoc = await getFirestoreDoc(firestoreDb, "arenas", targetArenaId);
        if (arenaDoc.exists && arenaDoc.data?.trialEndAt) {
          const parsed = new Date(arenaDoc.data.trialEndAt).getTime();
          if (!isNaN(parsed) && parsed > nowTime) {
            currentEndTimestamp = parsed;
          }
        }
      } catch (e) {}
    }

    const newEndTimestamp = currentEndTimestamp + thirtyDaysMs;
    const newEndAtIso = new Date(newEndTimestamp).toISOString();

    // 5. Persiste a atualização no Firestore / Estado da Arena
    const pdvUpdatePayload = {
      trialEndAt: newEndAtIso,
      trialStatus: "PDV_ACTIVE",
      commercialStatus: "PDV_ACTIVE",
      commercialPlanId: "plan_pdv_vendas_locais",
      financialModel: "PDV_VENDAS_LOCAIS", // Mantém o modelo PDV, não converte para FIXED_MONTHLY
      lastPdvPaymentDate: nowIso,
      updatedAt: nowIso,
    };

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(firestoreDb, "arenas", targetArenaId, pdvUpdatePayload, true);
        await setFirestoreDoc(firestoreDb, "arena_financial_configs", targetArenaId, pdvUpdatePayload, true);
      } catch (err: any) {
        console.error("[SubscriptionService] Erro ao persistir confirmação de PDV no Firestore:", err);
      }
    }

    // 6. Armazena no registro de confirmação para idempotência
    const record = {
      paymentId: paymentId || payKey,
      arenaId: targetArenaId,
      paidAmount: paidAmount !== undefined ? paidAmount : expectedAmount,
      newTrialEndAt: newEndAtIso,
      confirmedAt: nowIso,
    };
    if (paymentId) {
      inMemoryConfirmedPDVPayments.set(paymentId, record);
    }
    inMemoryConfirmedPDVPayments.set(payKey, record);

    return {
      success: true,
      confirmed: true,
      arenaId: targetArenaId,
      commercialStatus: "PDV_ACTIVE",
      daysRemaining: 30,
      newTrialEndAt: newEndAtIso,
      paidAmount: record.paidAmount,
      message: `Pagamento do Plano PDV confirmado com sucesso para a Arena ${targetArenaId}. Ciclo renovado por 30 dias.`,
    };
  }

  /**
   * Gera ou reutiliza a cobrança Pix de regularização da mensalidade da Arena.
   * Garante IDEMPOTÊNCIA: Reutiliza cobrança Pix aberta e válida se existir.
   * Não cria Pix duplicados para o mesmo ciclo.
   */
  static async regularizeSubscriptionPix(
    arenaId: string,
    firestoreDb?: any
  ): Promise<{
    success: boolean;
    message?: string;
    isPaid?: boolean;
    paymentId?: string;
    value?: number;
    dueDate?: string;
    qrCode?: string;
    qrCodeBase64?: string;
    copyPaste?: string;
    reusedExisting?: boolean;
    errorMessage?: string;
  }> {
    // 1. Obtém a assinatura da arena
    let sub = await this.getSubscriptionByArenaId(arenaId, firestoreDb);

    // Resolve o modelo comercial efetivo da arena
    let financialModel = sub?.financialModel;
    let hasExplicitConfig = !!sub?.financialModel;

    if (!financialModel && sub) {
      if (sub.planName?.toLowerCase().includes("hibrid") || sub.planId?.toLowerCase().includes("hybrid")) {
        financialModel = "HYBRID";
        hasExplicitConfig = true;
      } else if (sub.planName?.toLowerCase().includes("fix") || sub.planId?.toLowerCase().includes("fixed") || sub.value > 0) {
        financialModel = "FIXED_MONTHLY";
        hasExplicitConfig = true;
      }
    }

    if (!financialModel && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const cfgDoc = await getFirestoreDoc(firestoreDb, "arena_financial_configs", arenaId);
        if (cfgDoc && cfgDoc.exists && cfgDoc.data?.financialModel) {
          financialModel = cfgDoc.data.financialModel;
          hasExplicitConfig = true;
        }
      } catch (e) {}
    }

    if (!financialModel) {
      const resolved = resolveArenaFinancialModelSync({ arenaId, arenaData: {} });
      financialModel = resolved.model;
    }

    if (arenaId.toLowerCase().includes("no_data") || arenaId.toLowerCase().includes("no_email") || arenaId.toLowerCase().includes("no_name") || arenaId.toLowerCase().includes("unregistered")) {
      const realCustomer = await this.resolveRealCustomerData(arenaId, firestoreDb);
      if (!realCustomer.isValid) {
        return {
          success: false,
          errorMessage: "Dados cadastrais da Arena insuficientes para gerar a cobrança.",
        };
      }
    }

    if (financialModel === "SPLIT") {
      return {
        success: false,
        errorMessage: "Modelo SPLIT não possui taxa de mensalidade. Não há cobrança a regularizar.",
      };
    }

    // Valida dados cadastrais reais do proprietário/arena
    const realCustomer = await this.resolveRealCustomerData(arenaId, firestoreDb, sub ? {
      name: sub.customerName,
      email: sub.customerEmail,
      cpfCnpj: sub.customerCpfCnpj,
      customerId: sub.customerId,
    } : undefined);

    if (!realCustomer.isValid) {
      return {
        success: false,
        errorMessage: "Dados cadastrais da Arena insuficientes para gerar a cobrança.",
      };
    }

    // Se a assinatura ainda não existir no banco para FIXED_MONTHLY ou HYBRID, cria com plano padrão
    if (!sub) {
      const realCustomer = await this.resolveRealCustomerData(arenaId, firestoreDb);
      if (!realCustomer.isValid) {
        return {
          success: false,
          errorMessage: "Dados cadastrais da Arena insuficientes para gerar a cobrança.",
        };
      }

      const isPDVModel = financialModel === "PDV_VENDAS_LOCAIS";
      const pdvPlan = CommercialPlanService.getPDVPlan();
      const allPlans = await CommercialPlanService.listCommercialPlans();
      const plan = isPDVModel
        ? pdvPlan
        : (allPlans.find((p) => p.financialModel === financialModel) || allPlans[0]);
      const planValue = isPDVModel
        ? (pdvPlan.monthlyFee ?? pdvPlan.monthlyAmount ?? 0)
        : (plan.monthlyFee || 199.90);

      const createRes = await this.createSubscription(
        {
          userId: arenaId,
          arenaId,
          customerId: realCustomer.customerId,
          customerName: realCustomer.name,
          customerEmail: realCustomer.email,
          customerCpfCnpj: realCustomer.cpfCnpj,
          planId: plan.id,
          planName: plan.name,
          value: planValue,
          cycle: "MONTHLY",
          billingType: "PIX",
          description: `Mensalidade ${plan.name}`,
          financialModel: isPDVModel ? "PDV_VENDAS_LOCAIS" : financialModel,
        },
        firestoreDb
      );

      if (!createRes.success) {
        return { success: false, errorMessage: createRes.errorMessage || "Falha ao inicializar assinatura da arena." };
      }
      sub = await this.getSubscriptionByArenaId(arenaId, firestoreDb);
    }

    if (!sub) {
      return { success: false, errorMessage: "Assinatura não localizada para esta arena." };
    }

    // 2. IDEMPOTÊNCIA / DUPLICAÇÃO DE PIX: Verifica se existe pagamento PENDING/OVERDUE aberto e válido
    const existingPayments = inMemorySubscriptionPayments.get(sub.id) || [];
    let openPayment: SubscriptionPaymentRecord | null = null;

    for (const p of existingPayments) {
      if (p.status === "PENDING" || p.status === "OVERDUE") {
        openPayment = p;
        break;
      }
    }

    if (!openPayment && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const pDocs = await queryFirestoreDocs(
          firestoreDb,
          "subscription_payments",
          [
            { field: "subscriptionId", op: "==", value: sub.id },
            { field: "status", op: "in", value: ["PENDING", "OVERDUE"] },
          ],
          1
        );
        if (pDocs.length > 0) {
          openPayment = pDocs[0] as SubscriptionPaymentRecord;
        }
      } catch (err) {}
    }

    const gateway = PaymentGatewayFactory.getGateway(sub.gateway || "asaas");

    // Se já existe cobrança aberta para esta assinatura, REUTILIZA (IDEMPOTÊNCIA)
    if (openPayment && openPayment.gatewayPaymentId) {
      // Checa status atual no gateway
      const statusRes = await gateway.checkPaymentStatus(openPayment.gatewayPaymentId);
      if (statusRes.isPaid) {
        await this.recordSubscriptionPayment(
          {
            ...openPayment,
            status: "RECEIVED",
            paymentDate: new Date().toISOString(),
          },
          firestoreDb
        );
        return {
          success: true,
          isPaid: true,
          message: "Mensalidade confirmada e arena reativada com sucesso!",
        };
      }

      let qrCode = (openPayment as any).qrCode || "";
      let qrCodeBase64 = (openPayment as any).qrCodeBase64 || "";

      if (!qrCode && typeof (gateway as any).createPayment === "function") {
        try {
          const check = await gateway.checkPaymentStatus(openPayment.gatewayPaymentId);
          if (check.raw && check.raw.pixQrCode) {
            qrCode = check.raw.pixQrCode;
          }
        } catch (e) {}
      }

      if (qrCode) {
        return {
          success: true,
          isPaid: false,
          paymentId: openPayment.gatewayPaymentId,
          value: openPayment.value || sub.value,
          dueDate: openPayment.dueDate || sub.nextDueDate,
          qrCode,
          qrCodeBase64,
          copyPaste: qrCode,
          reusedExisting: true,
          message: "Cobrança Pix reutilizada com sucesso (idempotente).",
        };
      }
    }

    // 3. Se não existe cobrança aberta válida, CRIA NOVA cobrança no gateway (Asaas)
    const isPDVSub = sub.financialModel === "PDV_VENDAS_LOCAIS" || financialModel === "PDV_VENDAS_LOCAIS";
    const pdvCanonicalPlan = CommercialPlanService.getPDVPlan();
    const effectiveAmount = isPDVSub
      ? (pdvCanonicalPlan.monthlyFee ?? pdvCanonicalPlan.monthlyAmount ?? sub.value ?? 0)
      : (sub.value || 199.90);
    const effectiveExtRef = isPDVSub
      ? (sub.id.startsWith("pdv_") || sub.id.startsWith("plan_pdv_") ? sub.id : `pdv_${arenaId}`)
      : sub.id;

    const activeOwnerUid = sub.userId || realCustomer.customerId || arenaId;

    // ORDEM OBRIGATÓRIA PASSO 2: Criar pré-vínculo autoritativo em payment_charges ANTES de chamar o gateway
    const preChargeRecord: any = {
      id: effectiveExtRef,
      externalReference: effectiveExtRef,
      arenaId,
      userId: activeOwnerUid,
      ownerId: activeOwnerUid,
      arenaOwnerId: activeOwnerUid,
      userEmail: realCustomer.email,
      payerEmail: realCustomer.email,
      payerName: realCustomer.name,
      payerCpf: realCustomer.cpfCnpj || "",
      amount: effectiveAmount,
      value: effectiveAmount,
      billingType: "PIX",
      purpose: "PLAN_SUBSCRIPTION",
      recordType: "payment_intent",
      status: "AGUARDANDO_PAGAMENTO",
      paymentStatus: "AGUARDANDO_PAGAMENTO",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const isBrowserEnv = typeof window !== "undefined" && !(typeof process !== "undefined" && process.env?.NODE_ENV === "test");

    if (isBrowserEnv) {
      try {
        const authHeaders = await getAuthHeaders({ "Content-Type": "application/json" });
        await fetch("/api/plans/link-payment-charge", {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            externalReference: effectiveExtRef,
            paymentId: effectiveExtRef,
            arenaId,
            chargeData: preChargeRecord,
          }),
        });
      } catch (apiErr) {
        console.warn("[SubscriptionService] Erro ao registrar pré-vínculo via backend:", apiErr);
      }
    } else if (firestoreDb) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "payment_charges",
          effectiveExtRef,
          preChargeRecord,
          true
        );
      } catch (e) {
        console.warn("[SubscriptionService] Erro ao registrar pré-vínculo em payment_charges:", e);
      }
    }

    // ORDEM OBRIGATÓRIA PASSO 3: Criar Payment no Asaas
    const paymentReq: PaymentRequest = {
      amount: effectiveAmount,
      description: isPDVSub
        ? `Renovação Plano PDV / Vendas Locais - Arena ${realCustomer.name}`
        : `Mensalidade ${sub.planName || "Arena"} - Vencimento ${sub.nextDueDate}`,
      payerName: realCustomer.name,
      payerEmail: realCustomer.email,
      payerCpf: realCustomer.cpfCnpj || "",
      externalReference: effectiveExtRef,
      arenaId,
    };

    const newPayRes = await gateway.createPayment(paymentReq);
    if (!newPayRes.success || !newPayRes.paymentId) {
      return {
        success: false,
        errorMessage: newPayRes.errorMessage || "Falha ao emitir cobrança Pix no gateway.",
      };
    }

    // ORDEM OBRIGATÓRIA PASSO 4 & 5: Registrar o paymentId no vínculo autoritativo usando backend/Admin SDK
    const updatedChargeData: any = {
      ...preChargeRecord,
      paymentId: newPayRes.paymentId,
      gatewayPaymentId: newPayRes.paymentId,
      qrCode: newPayRes.qrCode || "",
      qrCodeBase64: newPayRes.qrCodeBase64 || "",
      invoiceUrl: newPayRes.invoiceUrl || null,
      updatedAt: new Date().toISOString(),
    };

    if (isBrowserEnv) {
      try {
        const authHeaders = await getAuthHeaders({ "Content-Type": "application/json" });
        await fetch("/api/plans/link-payment-charge", {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            externalReference: effectiveExtRef,
            paymentId: newPayRes.paymentId,
            arenaId,
            chargeData: updatedChargeData,
          }),
        });
      } catch (apiErr) {
        console.warn("[SubscriptionService] Erro ao vincular paymentId no backend:", apiErr);
      }
    } else if (firestoreDb) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "payment_charges",
          newPayRes.paymentId,
          updatedChargeData,
          true
        );
        await setFirestoreDoc(
          firestoreDb,
          "payment_charges",
          effectiveExtRef,
          updatedChargeData,
          true
        );
      } catch (e) {
        console.warn("[SubscriptionService] Erro ao vincular paymentId em payment_charges:", e);
      }
    }

    const newPaymentRecord: SubscriptionPaymentRecord = {
      id: newPayRes.paymentId,
      subscriptionId: sub.id,
      gateway: sub.gateway || "asaas",
      gatewayPaymentId: newPayRes.paymentId,
      gatewaySubscriptionId: sub.gatewaySubscriptionId || sub.id,
      value: sub.value,
      dueDate: sub.nextDueDate,
      status: "PENDING",
      billingType: "PIX",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      qrCode: newPayRes.qrCode,
      qrCodeBase64: newPayRes.qrCodeBase64,
    } as any;

    await this.recordSubscriptionPayment(newPaymentRecord, firestoreDb);

    return {
      success: true,
      isPaid: false,
      paymentId: newPayRes.paymentId,
      value: sub.value,
      dueDate: sub.nextDueDate,
      qrCode: newPayRes.qrCode,
      qrCodeBase64: newPayRes.qrCodeBase64,
      copyPaste: newPayRes.qrCode,
      reusedExisting: false,
      message: "Nova cobrança Pix gerada com sucesso.",
    };
  }

  /**
   * Checa o status de pagamento de uma mensalidade e reativa a arena se confirmado.
   */
  static async checkSubscriptionPaymentStatus(
    paymentId: string,
    firestoreDb?: any
  ): Promise<{ isPaid: boolean; status: string; message?: string }> {
    const gateway = PaymentGatewayFactory.getGateway("asaas");
    const check = await gateway.checkPaymentStatus(paymentId);

    if (check.isPaid) {
      // Localiza o registro de pagamento em memória ou Firestore
      let subId = "";
      for (const [sId, list] of inMemorySubscriptionPayments.entries()) {
        const found = list.find((p) => p.gatewayPaymentId === paymentId || p.id === paymentId);
        if (found) {
          subId = sId;
          break;
        }
      }

      if (subId) {
        await this.recordSubscriptionPayment(
          {
            id: paymentId,
            subscriptionId: subId,
            gateway: "asaas",
            gatewayPaymentId: paymentId,
            gatewaySubscriptionId: subId,
            value: check.value || 0,
            dueDate: new Date().toISOString().split("T")[0],
            paymentDate: new Date().toISOString(),
            status: "RECEIVED",
            billingType: "PIX",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          firestoreDb
        );
      }

      return { isPaid: true, status: "RECEIVED", message: "Pagamento confirmado! Arena reativada." };
    }

    return { isPaid: false, status: check.status || "PENDING" };
  }

  /**
   * Calcula o valor proporcional devido para alteração de modelo comercial de uma Arena.
   */
  static async calculateModelChangeProratedBalance(
    arenaId: string,
    requestedModel: ArenaFinancialModelType,
    firestoreDb?: any
  ): Promise<ProportionalCalculationResult> {
    const sub = await this.getSubscriptionByArenaId(arenaId, firestoreDb);
    let currentModel: ArenaFinancialModelType = "SPLIT";
    let monthlyValue = sub?.value || 0;

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const configDoc = await getFirestoreDoc(firestoreDb, "arena_financial_configs", arenaId);
        if (configDoc && configDoc.exists && configDoc.data) {
          const cfg = configDoc.data;
          currentModel = cfg.financialModel || "SPLIT";
          if (!monthlyValue && cfg.monthlyFee) {
            monthlyValue = cfg.monthlyFee;
          }
        }
      } catch (e) {}
    } else {
      const existingSub = sub as any;
      if (existingSub?.financialModel) {
        currentModel = existingSub.financialModel;
      }
    }

    return ProportionalFeeCalculator.calculateProportionalBalance({
      currentModel,
      requestedModel,
      monthlyValue,
      startDateIso: sub?.startDate,
      nextDueDateIso: sub?.nextDueDate,
      lastPaymentDateIso: sub?.lastPaymentDate,
      subscriptionStatus: sub?.status || "ACTIVE",
      requestDate: new Date(),
    });
  }

  /**
   * Verifica se a Arena possui mensalidades ou faturas de assinatura pendentes / vencidas.
   * Utilizado para impedir trocas silenciosas de modelo comercial quando há débitos contratuais em aberto.
   */
  static async checkPendingSubscriptionObligations(
    arenaId: string,
    firestoreDb?: any
  ): Promise<{
    hasPending: boolean;
    pendingAmount: number;
    pendingPaymentId?: string;
    subscriptionStatus?: string;
    message?: string;
  }> {
    if (!arenaId) {
      return { hasPending: false, pendingAmount: 0 };
    }

    const sub = await this.getSubscriptionByArenaId(arenaId, firestoreDb);
    const subStatus: string = sub?.status || "ACTIVE";

    let pendingAmount = 0;
    let pendingPaymentId: string | undefined;

    if (sub?.id && inMemorySubscriptionPayments.has(sub.id)) {
      const payments = inMemorySubscriptionPayments.get(sub.id) || [];
      for (const p of payments) {
        if (p.status === "PENDING" || p.status === "OVERDUE") {
          pendingAmount += p.value || 0;
          if (!pendingPaymentId) pendingPaymentId = p.id;
        }
      }
    }

    if (firestoreDb && sub?.id && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const pDocs = await queryFirestoreDocs(
          firestoreDb,
          "subscription_payments",
          [
            { field: "subscriptionId", op: "==", value: sub.id },
            { field: "status", op: "in", value: ["PENDING", "OVERDUE"] },
          ]
        );
        if (pDocs.length > 0) {
          for (const doc of pDocs) {
            const data = doc as SubscriptionPaymentRecord;
            pendingAmount += data.value || 0;
            if (!pendingPaymentId) pendingPaymentId = data.id;
          }
        }
      } catch (e) {}
    }

    let isOverdue = subStatus === "OVERDUE" || subStatus === "SUSPENDED" || pendingAmount > 0;
    if (pendingAmount === 0 && (subStatus === "OVERDUE" || subStatus === "SUSPENDED")) {
      pendingAmount = sub?.value || 0;
    }

    if (isOverdue && (pendingAmount > 0 || subStatus === "OVERDUE" || subStatus === "SUSPENDED")) {
      return {
        hasPending: true,
        pendingAmount,
        pendingPaymentId,
        subscriptionStatus: subStatus,
        message: `A Arena possui obrigações financeiras pendentes (R$ ${pendingAmount.toFixed(2)}). Regularize a mensalidade em aberto antes de alterar o modelo comercial.`,
      };
    }

    return {
      hasPending: false,
      pendingAmount: 0,
      subscriptionStatus: subStatus,
    };
  }

  /**
   * Solicita alteração de modelo comercial. Se houver saldo proporcional devido, gera/reutiliza Pix.
   * Enquanto a cobrança estiver pendente, o modelo efetivo PERMANECE o atual.
   */
  static async requestModelChangeWithProportionalPix(
    arenaId: string,
    requestedModel: ArenaFinancialModelType,
    user?: { id?: string; email?: string; name?: string },
    firestoreDb?: any
  ): Promise<{
    requiresPayment: boolean;
    calculation: ProportionalCalculationResult;
    pixCharge?: {
      paymentId: string;
      value: number;
      dueDate: string;
      qrCode?: string;
      qrCodeBase64?: string;
      copyPaste?: string;
    };
    success: boolean;
    message: string;
  }> {
    const calc = await this.calculateModelChangeProratedBalance(arenaId, requestedModel, firestoreDb);

    if (!calc.requiresPayment || calc.remainingBalanceDue <= 0) {
      // Sem saldo devido!
      return {
        requiresPayment: false,
        calculation: calc,
        success: true,
        message: calc.summaryMessage,
      };
    }

    // Existe saldo proporcional devido. O modelo efetivo DEVE PERMANECER o atual enquanto a cobrança estiver pendente.
    const realCustomer = await this.resolveRealCustomerData(arenaId, firestoreDb);
    if (!realCustomer.isValid) {
      return {
        requiresPayment: true,
        calculation: calc,
        success: false,
        message: "Dados cadastrais da Arena insuficientes para emitir a cobrança do valor proporcional.",
      };
    }

    const gateway = PaymentGatewayFactory.getGateway("asaas");
    const externalRef = `proportional_${arenaId}_${Date.now()}`;
    const activeOwnerUid = user?.id || realCustomer.customerId || arenaId;

    const preChargeRecord: any = {
      id: externalRef,
      externalReference: externalRef,
      arenaId,
      userId: activeOwnerUid,
      ownerId: activeOwnerUid,
      arenaOwnerId: activeOwnerUid,
      userEmail: realCustomer.email,
      payerEmail: realCustomer.email,
      payerName: realCustomer.name,
      payerCpf: realCustomer.cpfCnpj || "",
      amount: calc.remainingBalanceDue,
      value: calc.remainingBalanceDue,
      billingType: "PIX",
      purpose: "PLAN_CHANGE_PRORATED",
      recordType: "payment_intent",
      targetModel: requestedModel,
      previousModel: calc.currentModel,
      status: "AGUARDANDO_PAGAMENTO",
      paymentStatus: "AGUARDANDO_PAGAMENTO",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const isBrowserEnv = typeof window !== "undefined" && !(typeof process !== "undefined" && process.env?.NODE_ENV === "test");

    if (isBrowserEnv) {
      try {
        const authHeaders = await getAuthHeaders({ "Content-Type": "application/json" });
        await fetch("/api/plans/link-payment-charge", {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            externalReference: externalRef,
            paymentId: externalRef,
            arenaId,
            chargeData: preChargeRecord,
          }),
        });
      } catch (apiErr) {
        console.warn("[SubscriptionService] Erro ao registrar pré-vínculo de troca de plano via backend:", apiErr);
      }
    } else if (firestoreDb) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "payment_charges",
          externalRef,
          preChargeRecord,
          true
        );
      } catch (e) {
        console.warn("[SubscriptionService] Erro ao registrar pré-vínculo de troca em payment_charges:", e);
      }
    }

    const paymentReq: PaymentRequest = {
      amount: calc.remainingBalanceDue,
      description: `Ajuste Proporcional Mensalidade (${calc.currentModel} -> ${requestedModel}) - ${calc.daysConsidered} dias`,
      payerName: realCustomer.name,
      payerEmail: realCustomer.email,
      payerCpf: realCustomer.cpfCnpj || "",
      externalReference: externalRef,
      arenaId,
    };

    const newPayRes = await gateway.createPayment(paymentReq);
    if (!newPayRes.success || !newPayRes.paymentId) {
      return {
        requiresPayment: true,
        calculation: calc,
        success: false,
        message: newPayRes.errorMessage || "Falha ao emitir cobrança Pix proporcional no gateway.",
      };
    }

    const updatedChargeData: any = {
      ...preChargeRecord,
      paymentId: newPayRes.paymentId,
      gatewayPaymentId: newPayRes.paymentId,
      qrCode: newPayRes.qrCode || "",
      qrCodeBase64: newPayRes.qrCodeBase64 || "",
      invoiceUrl: newPayRes.invoiceUrl || null,
      updatedAt: new Date().toISOString(),
    };

    if (isBrowserEnv) {
      try {
        const authHeaders = await getAuthHeaders({ "Content-Type": "application/json" });
        await fetch("/api/plans/link-payment-charge", {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            externalReference: externalRef,
            paymentId: newPayRes.paymentId,
            arenaId,
            chargeData: updatedChargeData,
          }),
        });
      } catch (apiErr) {
        console.warn("[SubscriptionService] Erro ao vincular paymentId de troca no backend:", apiErr);
      }
    } else if (firestoreDb) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "payment_charges",
          newPayRes.paymentId,
          updatedChargeData,
          true
        );
        await setFirestoreDoc(
          firestoreDb,
          "payment_charges",
          externalRef,
          updatedChargeData,
          true
        );
      } catch (e) {
        console.warn("[SubscriptionService] Erro ao vincular paymentId de troca em payment_charges:", e);
      }
    }

    // Registra a solicitação de troca pendente com a cobrança gerada
    const pendingChangeRecord = {
      arenaId,
      currentModel: calc.currentModel,
      requestedModel,
      paymentId: newPayRes.paymentId,
      amountDue: calc.remainingBalanceDue,
      periodConsidered: calc.periodConsidered,
      daysConsidered: calc.daysConsidered,
      status: "PENDING_PAYMENT",
      createdAt: new Date().toISOString(),
      requestedBy: user?.email || user?.id || "owner",
    };

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "arena_financial_configs",
          arenaId,
          { pendingModelChange: pendingChangeRecord },
          true
        );
      } catch (e) {
        console.warn("[SubscriptionService] Erro ao salvar pendingModelChange no Firestore:", e);
      }
    }

    return {
      requiresPayment: true,
      calculation: calc,
      pixCharge: {
        paymentId: newPayRes.paymentId,
        value: calc.remainingBalanceDue,
        dueDate: new Date().toISOString().split("T")[0],
        qrCode: newPayRes.qrCode,
        qrCodeBase64: newPayRes.qrCodeBase64,
        copyPaste: newPayRes.qrCode,
      },
      success: true,
      message: calc.summaryMessage,
    };
  }

  /**
   * Confirma o pagamento do valor proporcional e efetiva a troca do modelo comercial.
   */
  static async confirmProportionalPaymentAndSwitchModel(
    paymentId: string,
    arenaId: string,
    requestedModel: ArenaFinancialModelType,
    user?: { id?: string; email?: string; name?: string },
    firestoreDb?: any
  ): Promise<{ success: boolean; isPaid: boolean; message: string }> {
    const gateway = PaymentGatewayFactory.getGateway("asaas");
    const check = await gateway.checkPaymentStatus(paymentId);

    if (!check.isPaid) {
      return {
        success: false,
        isPaid: false,
        message: "O pagamento do ajuste proporcional ainda não foi confirmado pelo gateway.",
      };
    }

    // Pagamento confirmado! Efetiva a alteração do modelo na arena
    await saveArenaFinancialConfig(
      arenaId,
      { financialModel: requestedModel },
      user,
      `Troca de modelo comercial para ${requestedModel} após liquidação do ajuste proporcional (Pagamento Pix ${paymentId})`,
      firestoreDb
    );

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "arena_financial_configs",
          arenaId,
          { pendingModelChange: null },
          true
        );
      } catch (e) {}
    }

    return {
      success: true,
      isPaid: true,
      message: `Ajuste proporcional pago com sucesso! Modelo comercial atualizado para ${requestedModel}.`,
    };
  }

  /**
   * BLOCO 12G: Sincroniza/inicia o ciclo mensal da Arena após ativação de um modelo comercial.
   * - FIXED_MONTHLY: inicia ou ajusta o ciclo mensal de R$ 200 (ou valor configurado), 0% comissão, status ACTIVE, lastPaymentDate = now.
   * - HYBRID: inicia ou ajusta o ciclo mensal de R$ 99 (ou valor configurado), comissão (ex: 2%), status ACTIVE, lastPaymentDate = now.
   * - SPLIT: não cria mensalidade recorrente (cancela ou desativa assinatura de mensalidade anterior se houver).
   */
  static async syncModelChangeSubscription(params: {
    arenaId: string;
    targetModel: ArenaFinancialModelType;
    monthlyFee?: number;
    commissionRate?: number;
    paymentId?: string;
    paidAmount?: number;
    firestoreDb?: any;
    now?: Date | string;
  }): Promise<{ success: boolean; subscriptionId?: string; message: string }> {
    const { arenaId, targetModel, monthlyFee = 0, paymentId, paidAmount, firestoreDb } = params;
    const nowIso = params.now ? (typeof params.now === "string" ? params.now : params.now.toISOString()) : new Date().toISOString();
    const nextDueDate = new Date(new Date(nowIso).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    if (!arenaId) {
      return { success: false, message: "arenaId é obrigatório" };
    }

    if (targetModel === "SPLIT") {
      const existing = await this.getSubscriptionByArenaId(arenaId, firestoreDb);
      if (existing) {
        existing.status = "CANCELLED";
        existing.updatedAt = nowIso;
        inMemorySubscriptions.set(existing.id, existing);
        if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
          try {
            await setFirestoreDoc(firestoreDb, "subscriptions", existing.id, { status: "CANCELLED", updatedAt: nowIso }, true);
          } catch (e) {}
        }
      }
      return { success: true, message: "Modelo SPLIT ativado: sem mensalidade recorrente." };
    }

    if (targetModel === "FIXED_MONTHLY" || targetModel === "HYBRID") {
      const existing = await this.getSubscriptionByArenaId(arenaId, firestoreDb);
      const subId = existing?.id || `sub_${targetModel.toLowerCase()}_${arenaId}`;
      const effectiveValue = monthlyFee > 0 ? monthlyFee : (targetModel === "FIXED_MONTHLY" ? 200 : 99);

      const subRecord: SubscriptionRecord = {
        id: subId,
        arenaId,
        planId: targetModel === "FIXED_MONTHLY" ? "fixed_1_2" : "hybrid_1_3",
        planName: targetModel === "FIXED_MONTHLY" ? "Plano Mensalidade Fixa" : "Plano Híbrido",
        gateway: "asaas",
        gatewaySubscriptionId: subId,
        value: effectiveValue,
        cycle: "MONTHLY",
        status: "ACTIVE",
        startDate: nowIso,
        nextDueDate: nextDueDate,
        lastPaymentDate: nowIso,
        financialModel: targetModel,
        createdAt: existing?.createdAt || nowIso,
        updatedAt: nowIso,
      };

      inMemorySubscriptions.set(subId, subRecord);

      if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          await setFirestoreDoc(firestoreDb, "subscriptions", subId, subRecord, true);
        } catch (e) {
          console.warn("[SubscriptionService] Erro ao salvar subscription no Firestore:", e);
        }
      }

    if (paymentId) {
        const existingPayments = await this.getSubscriptionPayments(subId, firestoreDb);
        if (existingPayments.some((p) => p.gatewayPaymentId === paymentId)) {
          return {
            success: true,
            subscriptionId: subId,
            message: `Pagamento ${paymentId} já processado. Idempotência mantida.`,
          };
        }
        const payRec: SubscriptionPaymentRecord = {
          id: paymentId,
          subscriptionId: subId,
          gateway: "asaas",
          gatewayPaymentId: paymentId,
          gatewaySubscriptionId: subId,
          value: paidAmount !== undefined ? paidAmount : effectiveValue,
          dueDate: nowIso.split("T")[0],
          paymentDate: nowIso,
          status: "RECEIVED",
          createdAt: nowIso,
          updatedAt: nowIso,
        };
        try {
          await this.recordSubscriptionPayment(payRec, firestoreDb);
        } catch (e) {}

        const cycleId = `cycle_${subId}_${paymentId}`;
        const existingCycles = inMemorySubscriptionCycles.get(subId) || [];
        if (!existingCycles.some(c => c.id === cycleId)) {
          const newCycle: SubscriptionCycleRecord = {
            id: cycleId,
            subscriptionId: subId,
            arenaId,
            commercialModel: targetModel,
            cycleNumber: existingCycles.length + 1,
            startDate: nowIso,
            dueDate: nextDueDate,
            amount: effectiveValue,
            status: "ACTIVE",
            createdAt: nowIso,
            updatedAt: nowIso,
          };
          existingCycles.push(newCycle);
          inMemorySubscriptionCycles.set(subId, existingCycles);
          if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
            try {
              await setFirestoreDoc(firestoreDb, "subscription_cycles", cycleId, newCycle, true);
            } catch (e) {}
          }
        }
      }

      return {
        success: true,
        subscriptionId: subId,
        message: `Ciclo ${targetModel} inicializado com sucesso para a Arena ${arenaId}.`,
      };
    }

    return { success: true, message: `Modelo ${targetModel} sincronizado.` };
  }

  /**
   * BLOCO 12D: Obtém e avalia o ciclo mensal de faturamento para o modelo FIXED_MONTHLY (ou outros modelos).
   * Retorna os dados do ciclo: início, vencimento, valor configurado, status, elegibilidade operacional e último pagamento.
   */
  static async getArenaMonthlyCycle(
    arenaId: string,
    options?: { now?: Date | string | number; firestoreDb?: any }
  ): Promise<ArenaMonthlyCycle> {
    const firestoreDb = options?.firestoreDb;
    const nowInput = options?.now;
    const nowDate = nowInput ? (nowInput instanceof Date ? nowInput : new Date(nowInput)) : new Date();

    if (!arenaId || arenaId === "arena_padrao" || arenaId === "minha_arena") {
      return {
        arenaId,
        financialModel: "SPLIT",
        startDate: "",
        dueDate: "",
        value: 0,
        status: "NOT_APPLICABLE",
        isOperational: true,
        lastPaymentDate: null,
      };
    }

    const localProfile: any = UserProfileRepository.getActiveProfile() || UserProfileRepository.getProfile<any>() || {};

    // 1. Busca a assinatura ativa ou existente da arena
    const sub = await this.getSubscriptionByArenaId(arenaId, firestoreDb);

    // 2. Resolve o modelo financeiro e configurações comerciais canônicas da arena
    const resolvedConfig = resolveArenaFinancialModelSync({ arenaId, arenaData: {} });
    let effectiveModel: string | undefined = undefined;
    let effectiveMonthlyFee = resolvedConfig.monthlyFee || 0;
    let effectiveCommissionRate = resolvedConfig.commissionRate ?? 0;

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const cfgDoc = await getFirestoreDoc(firestoreDb, "arena_financial_configs", arenaId);
        if (cfgDoc && cfgDoc.exists && cfgDoc.data) {
          if (cfgDoc.data.financialModel) {
            effectiveModel = cfgDoc.data.financialModel;
          }
          if (cfgDoc.data.monthlyFee !== undefined) {
            effectiveMonthlyFee = cfgDoc.data.monthlyFee;
          }
          if (cfgDoc.data.commissionRate !== undefined) {
            effectiveCommissionRate = cfgDoc.data.commissionRate;
          }
        }
      } catch (e) {}
    }

    let activeCourtesy: any = null;
    try {
      const courtesyConfig = await CommercialPlanService.getArenaCourtesyConfig(arenaId, firestoreDb);
      if (courtesyConfig && courtesyConfig.enabled && courtesyConfig.model) {
        activeCourtesy = courtesyConfig;
        effectiveModel = courtesyConfig.model;
        effectiveMonthlyFee = 0;
      }
    } catch (e) {}

    if (!effectiveModel) {
      effectiveModel = sub?.financialModel || resolvedConfig.model;
    }

    const finalCommissionRate = effectiveModel === "FIXED_MONTHLY" ? 0 : effectiveCommissionRate;

    // Se o modelo for SPLIT (sem mensalidade):
    if (effectiveModel === "SPLIT") {
      return {
        arenaId,
        subscriptionId: sub?.id,
        financialModel: "SPLIT",
        startDate: sub?.startDate || "",
        dueDate: sub?.nextDueDate || "",
        value: 0,
        monthlyAmount: 0,
        commissionRate: finalCommissionRate,
        status: "NOT_APPLICABLE",
        isOperational: true,
        lastPaymentDate: sub?.lastPaymentDate || null,
      };
    }

    // Se o modelo for FIXED_MONTHLY ou HYBRID (com ciclo mensal):
    if (sub) {
      const startDate = sub.startDate || sub.createdAt || nowDate.toISOString();
      const dueDate = sub.nextDueDate || startDate.split("T")[0];
      const value = sub.value !== undefined ? sub.value : effectiveMonthlyFee;
      const monthlyAmount = value;
      const lastPaymentDate = sub.lastPaymentDate || null;

      // Busca pagamentos relacionados a esta assinatura
      const payments = inMemorySubscriptionPayments.get(sub.id) || [];
      if (payments.length === 0 && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          const pDocs = await queryFirestoreDocs(firestoreDb, "subscription_payments", [
            { field: "subscriptionId", op: "==", value: sub.id }
          ]);
          if (pDocs.length > 0) {
            payments.push(...(pDocs as SubscriptionPaymentRecord[]));
          }
        } catch (e) {}
      }

      // Avalia tolerância de 4 HORAS pós-vencimento
      const grace = this.calculateGracePeriodStatus(dueDate, nowDate);

      // Verifica se existe pagamento confirmado/recebido para o ciclo atual ou se há cortesia ativa
      const hasConfirmedPayment = Boolean(activeCourtesy) || payments.some(
        (p) =>
          (p.status === "RECEIVED" || p.status === "CONFIRMED") &&
          (p.dueDate === dueDate || !p.dueDate || (p.paymentDate && p.paymentDate >= startDate))
      ) || Boolean(sub.lastPaymentDate && (sub.lastPaymentDate >= startDate || sub.lastPaymentDate >= dueDate) && sub.status === "ACTIVE");

      let status: ArenaCycleStatus = "PENDING";
      let isOperational = true;

      if (hasConfirmedPayment) {
        status = "PAID";
        isOperational = true;
      } else if (grace.daysOverdue <= 0 && grace.hoursOverdue <= 0 && grace.state === "ACTIVE") {
        status = "PENDING";
        isOperational = true;
      } else if (grace.isGracePeriod) {
        status = "GRACE";
        isOperational = true;
      } else {
        status = "OVERDUE";
        isOperational = false;
      }

      const matchedPayment = payments.find(p => p.dueDate === dueDate || (p.paymentDate && p.paymentDate >= startDate));

      return {
        arenaId,
        subscriptionId: sub.id,
        cycleId: `cycle_${sub.id}_current`,
        financialModel: effectiveModel || sub.financialModel || "FIXED_MONTHLY",
        startDate,
        dueDate,
        endDate: dueDate,
        value,
        monthlyAmount,
        commissionRate: finalCommissionRate,
        status,
        isOperational,
        lastPaymentDate,
        daysOverdue: grace.daysOverdue,
        hoursOverdue: grace.hoursOverdue,
        isGracePeriod: grace.isGracePeriod,
        commercialModel: effectiveModel || sub.financialModel || "FIXED_MONTHLY",
        commercialPlanId: sub.planId || (effectiveModel === "FIXED_MONTHLY" ? "fixed_1_2" : "hybrid_1_3"),
        paymentId: matchedPayment?.id || undefined,
        paymentReference: matchedPayment?.gatewayPaymentId || undefined,
        chargeStatus: matchedPayment?.status || (hasConfirmedPayment ? "CONFIRMED" : "PENDING"),
      };
    }

    // Caso não haja assinatura explícita mas a arena esteja configurada para FIXED_MONTHLY ou HYBRID
    const nowIso = nowDate.toISOString();
    const defaultDueDate = new Date(nowDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    const defaultVal = effectiveMonthlyFee;
    return {
      arenaId,
      financialModel: effectiveModel || resolvedConfig.model || "FIXED_MONTHLY",
      startDate: nowIso,
      dueDate: defaultDueDate,
      endDate: defaultDueDate, // Compatibilidade com endDate
      value: defaultVal,
      monthlyAmount: defaultVal,
      commissionRate: finalCommissionRate,
      status: "PENDING",
      isOperational: true,
      lastPaymentDate: null,
      daysOverdue: 0,
      isGracePeriod: false,
      commercialModel: effectiveModel || resolvedConfig.model || "FIXED_MONTHLY", // Compatibilidade com commercialModel
      commercialPlanId: localProfile?.commercialPlanId || "split_1_open", // Compatibilidade com commercialPlanId
    };
  }

  /**
   * BLOCO 12D: Avalia se uma Arena está elegível operacionalmente para reservas com base em seu modelo e ciclo.
   */
  static async isArenaOperationallyEligible(
    arenaId: string,
    options?: { now?: Date | string | number; firestoreDb?: any }
  ): Promise<boolean> {
    const cycle = await this.getArenaMonthlyCycle(arenaId, options);
    return cycle.isOperational;
  }

  /**
   * BLOCO 10 / BLOCO 12D: Renova o ciclo mensal de uma assinatura existente após pagamento confirmado.
   * Regras:
   * - Apenas FIXED_MONTHLY e HYBRID renovam (SPLIT não possui renovação mensal nem gera cobrança);
   * - Idempotente por paymentId e vigência;
   * - Preserva histórico do ciclo anterior (valor histórico imutável);
   * - Novo ciclo utiliza o preço efetivo atual da Arena via getEffectivePlanPrice();
   * - HYBRID preserva monthlyAmount e commissionRate separados;
   * - Se houver cortesia ativa, não gera renovação paga enquanto durar a cortesia.
   */
  static async renewSubscriptionCycle(
    arenaIdOrSubId: string,
    options?: {
      nextDueDate?: string;
      firestoreDb?: any;
      now?: Date;
      paymentId?: string;
      paymentReference?: string;
      paidAmount?: number;
      status?: ArenaCycleStatus;
    }
  ): Promise<{
    success: boolean;
    subscription?: SubscriptionRecord;
    newCycle?: SubscriptionCycleRecord;
    alreadyRenewed?: boolean;
    isCourtesyActive?: boolean;
    message?: string;
  }> {
    const firestoreDb = options?.firestoreDb;
    const nowDate = options?.now || new Date();
    const nowIso = nowDate.toISOString();

    let sub: SubscriptionRecord | null = null;
    if (inMemorySubscriptions.has(arenaIdOrSubId)) {
      sub = inMemorySubscriptions.get(arenaIdOrSubId)!;
    } else {
      sub = await this.getSubscriptionByArenaId(arenaIdOrSubId, firestoreDb);
    }

    if (!sub && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const snap = await getFirestoreDoc(firestoreDb, "subscriptions", arenaIdOrSubId);
        if (snap.exists && snap.data) {
          sub = snap.data as SubscriptionRecord;
        }
      } catch (e) {}
    }

    if (!sub) {
      // Verifica se a arena opera em SPLIT
      const resolvedConfig = resolveArenaFinancialModelSync({ arenaId: arenaIdOrSubId, arenaData: {} });
      if (resolvedConfig.model === "SPLIT") {
        return {
          success: false,
          message: "Modelo SPLIT não possui ciclo ou renovação mensal.",
        };
      }
      return {
        success: false,
        message: `Assinatura ${arenaIdOrSubId} não encontrada para renovação.`,
      };
    }

    // Regra 3 e 4: SPLIT não possui renovação mensal e não gera ciclo
    if (sub.financialModel === "SPLIT") {
      return {
        success: false,
        message: "Modelo SPLIT não possui ciclo ou renovação mensal.",
      };
    }

    const subId = sub.id;
    const arenaId = sub.arenaId || sub.userId;

    // Regra 8: Se existir cortesia ativa para a Arena, não gera cobrança de renovação paga
    const courtesy = await CommercialPlanService.getArenaCourtesyConfig(arenaId, firestoreDb);
    if (courtesy && courtesy.enabled) {
      const courtesyEnd = new Date(courtesy.endDate + (courtesy.endDate.includes("T") ? "" : "T23:59:59Z"));
      if (courtesyEnd >= nowDate) {
        return {
          success: false,
          isCourtesyActive: true,
          message: `Arena ${arenaId} possui cortesia ativa até ${courtesy.endDate}. Não gerar renovação paga enquanto a cortesia estiver vigente.`,
        };
      }
    }

    // 1. Recupera o histórico de ciclos existentes
    let cycles = inMemorySubscriptionCycles.get(subId) || [];
    if (cycles.length === 0 && firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const cDocs = await queryFirestoreDocs(firestoreDb, "subscription_cycles", [
          { field: "subscriptionId", op: "==", value: subId }
        ]);
        if (cDocs.length > 0) {
          cycles = cDocs as SubscriptionCycleRecord[];
        }
      } catch (e) {}
    }

    // Regra 10: Idempotência se paymentId já foi processado
    if (options?.paymentId) {
      if (
        processedRenewalPaymentIds.has(options.paymentId) ||
        (sub as any).lastRenewalPaymentId === options.paymentId ||
        cycles.some((c) => (c as any).paymentId === options.paymentId)
      ) {
        return {
          success: true,
          subscription: sub,
          alreadyRenewed: true,
          message: `Pagamento ${options.paymentId} já havia sido processado para esta renovação (idempotente).`,
        };
      }
    }

    // Idempotência se não há paymentId e já há vigência ativa/futura
    const nowDayStr = nowDate.toISOString().split("T")[0];
    if (!options?.paymentId) {
      if ((options?.nextDueDate && sub.nextDueDate >= options.nextDueDate) || sub.nextDueDate > nowDayStr) {
        return {
          success: true,
          subscription: sub,
          message: `Assinatura ${sub.id} já possui vigência ativa ou futura até ${sub.nextDueDate}.`,
        };
      }
    }

    // 2. Arquiva o ciclo anterior no histórico preservando o valor histórico (Regras 5 e 6)
    const resolvedConfig = resolveArenaFinancialModelSync({
      arenaId,
      arenaData: { financialModel: sub.financialModel as ArenaFinancialModelType, commercialPlanId: sub.planId },
    });
    let effectiveCommissionRate = 0;
    if (sub.financialModel === "HYBRID") {
      if (resolvedConfig.model === "HYBRID" && typeof resolvedConfig.commissionRate === "number") {
        effectiveCommissionRate = resolvedConfig.commissionRate;
      } else {
        const plan = DEFAULT_COMMERCIAL_PLANS.find((p) => p.id === sub.planId);
        effectiveCommissionRate = plan?.commissionRate ?? 2;
      }
    }
    const prevCycleNumber = cycles.length + 1;
    const prevStartDate = sub.startDate || sub.createdAt || nowIso;
    const prevDueDate = sub.nextDueDate || prevStartDate.split("T")[0];

    const alreadyArchived = cycles.some(
      (c) => c.startDate === prevStartDate && c.dueDate === prevDueDate
    );

    if (!alreadyArchived) {
      const prevCycleRecord: SubscriptionCycleRecord = {
        id: `cycle_${subId}_${prevCycleNumber}`,
        cycleId: `cycle_${subId}_${prevCycleNumber}`,
        subscriptionId: subId,
        arenaId,
        cycleNumber: prevCycleNumber,
        startDate: prevStartDate,
        dueDate: prevDueDate,
        endDate: prevDueDate,
        value: sub.value, // Preserva valor histórico anterior
        monthlyAmount: sub.value, // Preserva valor histórico anterior
        commissionRate: effectiveCommissionRate,
        status: (sub.status === "ACTIVE" ? "PAID" : "EXPIRED") as ArenaCycleStatus,
        lastPaymentDate: sub.lastPaymentDate || null,
        commercialModel: sub.financialModel,
        commercialPlanId: sub.planId,
        createdAt: prevStartDate,
        updatedAt: nowIso,
      };
      cycles.push(prevCycleRecord);
      inMemorySubscriptionCycles.set(subId, cycles);

      if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
        try {
          await setFirestoreDoc(firestoreDb, "subscription_cycles", prevCycleRecord.id, prevCycleRecord, true);
        } catch (e) {
          console.warn("[SubscriptionService] Erro ao arquivar ciclo no Firestore:", e);
        }
      }
    }

    // 3. Calcula as novas datas para o próximo ciclo
    const newStartDate = prevDueDate;
    let newDueDate = options?.nextDueDate;

    if (!newDueDate) {
      const startDay = new Date(`${newStartDate}T00:00:00Z`);
      if (!isNaN(startDay.getTime())) {
        const nextMonth = new Date(startDay);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        newDueDate = nextMonth.toISOString().split("T")[0];
      } else {
        newDueDate = new Date(nowDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
      }
    }

    // Regra 6: Resolução canônica do preço efetivo no momento da renovação
    let effectiveNewPrice = sub.value;
    try {
      const effectiveRes = await CommercialPlanService.getEffectivePlanPrice(
        arenaId,
        sub.financialModel as ArenaFinancialModelType,
        firestoreDb
      );
      if (effectiveRes && typeof effectiveRes.price === "number") {
        if (effectiveRes.source === "ARENA_CUSTOM") {
          // Preço personalizado vigente da Arena
          effectiveNewPrice = effectiveRes.price;
        } else if (
          (sub.planId && (sub.planId.startsWith("fixed_") || sub.planId.startsWith("hybrid_"))) ||
          sub.value === undefined ||
          sub.value === null ||
          sub.value <= 0
        ) {
          // Se for plano padrão de catálogo ou valor não definido, adota preço global
          effectiveNewPrice = effectiveRes.price;
        }
      }
    } catch (e) {
      console.warn("[SubscriptionService] Erro ao obter preço efetivo na renovação:", e);
    }

    // 4. Cria o novo ciclo ativo (Regras 5, 6 e 7)
    const nextCycleNumber = cycles.length + 1;
    const newCycleId = `cycle_${subId}_${nextCycleNumber}`;
    const newCycleStatus: ArenaCycleStatus = options?.status || (options?.paymentId ? "PAID" : "PENDING");
    const paymentTimestamp = options?.paymentId ? (options?.now ? options.now.toISOString() : nowIso) : null;

    const newCycleRecord: SubscriptionCycleRecord = {
      id: newCycleId,
      cycleId: newCycleId,
      subscriptionId: subId,
      arenaId,
      cycleNumber: nextCycleNumber,
      startDate: newStartDate,
      dueDate: newDueDate,
      endDate: newDueDate,
      value: effectiveNewPrice, // Preço efetivo atual da Arena
      monthlyAmount: effectiveNewPrice, // Componente mensal fixo
      commissionRate: effectiveCommissionRate, // Comissão separada
      status: newCycleStatus,
      paymentId: options?.paymentId || null,
      paymentReference: options?.paymentReference || options?.paymentId || null,
      lastPaymentDate: paymentTimestamp,
      commercialModel: sub.financialModel,
      commercialPlanId: sub.planId,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    if (options?.paymentId) {
      processedRenewalPaymentIds.add(options.paymentId);
      (sub as any).lastRenewalPaymentId = options.paymentId;
    }

    // 5. Atualiza a assinatura pai
    sub.value = effectiveNewPrice;
    sub.startDate = newStartDate;
    sub.nextDueDate = newDueDate;
    sub.status = "ACTIVE";
    sub.updatedAt = nowIso;
    if (options?.paymentId) {
      sub.lastPaymentDate = paymentTimestamp;
    }
    inMemorySubscriptions.set(subId, sub);

    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        await setFirestoreDoc(
          firestoreDb,
          "subscriptions",
          subId,
          {
            value: effectiveNewPrice,
            startDate: newStartDate,
            nextDueDate: newDueDate,
            status: "ACTIVE",
            lastPaymentDate: sub.lastPaymentDate,
            updatedAt: nowIso,
          },
          true
        );
      } catch (e) {
        console.warn("[SubscriptionService] Erro ao atualizar assinatura no Firestore durante renovação:", e);
      }
    }

    return {
      success: true,
      subscription: sub,
      newCycle: newCycleRecord,
      message: `Ciclo renovado com sucesso para o período ${newStartDate} até ${newDueDate}.`,
    };
  }

  /**
   * BLOCO 10: Confirma o pagamento de renovação de uma mensalidade e renova o ciclo operacional.
   */
  static async confirmSubscriptionRenewalPayment(params: {
    paymentId: string;
    externalReference?: string;
    subscriptionId?: string;
    arenaId?: string;
    paidAmount?: number;
    paymentStatus?: string;
    now?: Date;
    firestoreDb?: any;
  }): Promise<{
    success: boolean;
    renewed: boolean;
    alreadyRenewed?: boolean;
    isCourtesyActive?: boolean;
    cycle?: SubscriptionCycleRecord;
    message: string;
  }> {
    const {
      paymentId,
      externalReference,
      subscriptionId,
      arenaId,
      paidAmount,
      paymentStatus = "RECEIVED",
      now = new Date(),
      firestoreDb,
    } = params;

    const normalizedStatus = String(paymentStatus).toUpperCase();
    const isApproved = normalizedStatus === "RECEIVED" || normalizedStatus === "CONFIRMED" || normalizedStatus === "PAID";

    if (!isApproved) {
      return {
        success: false,
        renewed: false,
        message: `Pagamento ${paymentId} com status '${paymentStatus}' não é elegível para renovação de ciclo.`,
      };
    }

    let targetSubId = subscriptionId;
    if (!targetSubId && arenaId) {
      const sub = await this.getSubscriptionByArenaId(arenaId, firestoreDb);
      if (sub) targetSubId = sub.id;
    }

    if (!targetSubId && externalReference) {
      for (const s of inMemorySubscriptions.values()) {
        if (s.id === externalReference || s.arenaId === externalReference || s.gatewaySubscriptionId === externalReference) {
          targetSubId = s.id;
          break;
        }
      }
    }

    if (!targetSubId && arenaId) {
      targetSubId = arenaId;
    }

    if (!targetSubId) {
      return {
        success: false,
        renewed: false,
        message: "Assinatura ou Arena não identificada para o pagamento.",
      };
    }

    const renewResult = await this.renewSubscriptionCycle(targetSubId, {
      paymentId,
      paymentReference: externalReference,
      paidAmount,
      now,
      firestoreDb,
    });

    return {
      success: renewResult.success,
      renewed: renewResult.success && !renewResult.alreadyRenewed,
      alreadyRenewed: renewResult.alreadyRenewed,
      isCourtesyActive: renewResult.isCourtesyActive,
      cycle: renewResult.newCycle,
      message: renewResult.message || "Renovação processada.",
    };
  }

  /**
   * BLOCO 12D: Obtém o histórico completo de ciclos de uma assinatura / arena.
   */
  static async getSubscriptionCycleHistory(
    arenaIdOrSubId: string,
    firestoreDb?: any
  ): Promise<SubscriptionCycleRecord[]> {
    let subId = arenaIdOrSubId;
    let arenaId = arenaIdOrSubId;

    const sub = inMemorySubscriptions.get(arenaIdOrSubId) || (await this.getSubscriptionByArenaId(arenaIdOrSubId, firestoreDb));
    if (sub) {
      subId = sub.id;
      arenaId = sub.arenaId || sub.userId;
    }

    // 1. Memória: busca ciclos relacionados tanto a subId quanto a arenaId
    const memCyclesMap = new Map<string, SubscriptionCycleRecord>();
    for (const [key, list] of inMemorySubscriptionCycles.entries()) {
      if (key === subId || key === arenaId) {
        for (const c of list) {
          if (c && c.id) memCyclesMap.set(c.id, c);
        }
      } else {
        for (const c of list) {
          if (c && (c.arenaId === arenaId || c.subscriptionId === subId)) {
            memCyclesMap.set(c.id, c);
          }
        }
      }
    }
    const memCycles = Array.from(memCyclesMap.values());

    // 2. Firestore
    if (firestoreDb && (isV8Db(firestoreDb) || resolveModularDb(firestoreDb))) {
      try {
        const cDocsSub = await queryFirestoreDocs(firestoreDb, "subscription_cycles", [
          { field: "subscriptionId", op: "==", value: subId }
        ]);
        const cDocsArena = subId !== arenaId ? await queryFirestoreDocs(firestoreDb, "subscription_cycles", [
          { field: "arenaId", op: "==", value: arenaId }
        ]) : [];
        const allDocs = [...cDocsSub, ...cDocsArena];
        if (allDocs.length > 0) {
          const merged = [...memCycles];
          for (const doc of allDocs) {
            if (!merged.some((m) => m.id === doc.id)) {
              merged.push(doc as SubscriptionCycleRecord);
            }
          }
          return merged.sort((a, b) => (a.cycleNumber || 0) - (b.cycleNumber || 0));
        }
      } catch (e) {}
    }

    return [...memCycles].sort((a, b) => (a.cycleNumber || 0) - (b.cycleNumber || 0));
  }

  /**
   * BLOCO COMERCIAL 7B: Aplica o ciclo comercial operacional resolvido para o estado operacional real.
   * Consome o resultado de resolveOperationalCommercialCycle e atualiza a configuração
   * da arena através do writer canônico existente saveArenaFinancialConfig.
   * Não cria novas assinaturas, ciclos ou pagamentos.
   * Mantém idempotência estrita (evita gravações redundantes se o estado já estiver aplicado).
   */
  static async applyOperationalCommercialCycle(
    arenaId: string,
    options?: {
      now?: Date;
      resolution?: OperationalCycleResolution;
      user?: any;
      firestoreDb?: any;
    }
  ): Promise<{
    success: boolean;
    applied: boolean;
    resolution: OperationalCycleResolution;
    config?: any;
    message: string;
  }> {
    if (!arenaId || typeof arenaId !== "string" || !arenaId.trim()) {
      throw new Error("arenaId é obrigatório para aplicar ciclo operacional.");
    }

    // 1. Resolve o ciclo operacional vigente (ou usa resolução fornecida)
    const resolution = options?.resolution || (await resolveOperationalCommercialCycle(arenaId, options?.now));

    // 2. Obtém a configuração atual para verificar idempotência
    const currentConfig = resolveArenaFinancialModelSync({ arenaId, arenaData: {} });

    // 3. Monta o payload canônico para saveArenaFinancialConfig
    const targetModel = resolution.model as ArenaFinancialModelType;
    const targetPlanId = resolution.planId;

    let targetMonthlyFee = 0;
    let targetCommissionRate = 0;

    if (targetModel === "SPLIT") {
      targetMonthlyFee = 0;
      targetCommissionRate = 4.0;
    } else if (targetModel === "FIXED_MONTHLY") {
      const planDef = DEFAULT_COMMERCIAL_PLANS.find((p) => p.id === targetPlanId);
      targetMonthlyFee = resolution.monthlyAmount !== undefined ? resolution.monthlyAmount : (planDef?.monthlyFee ?? 200);
      targetCommissionRate = 0;
    } else if (targetModel === "HYBRID") {
      const planDef = DEFAULT_COMMERCIAL_PLANS.find((p) => p.id === targetPlanId);
      targetMonthlyFee = resolution.monthlyAmount !== undefined ? resolution.monthlyAmount : (planDef?.monthlyFee ?? 99);
      targetCommissionRate = planDef?.commissionRate ?? 2.0;
    }

    // 4. Verificação de idempotência e proteção de modelo explícito
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {}) || {};
    const currentStored = storedConfigs[arenaId];

    const hasExplicitArenaModel = currentConfig.source === "ARENA" &&
      ["FIXED_MONTHLY", "HYBRID", "OPERATION_ONLY", "SPLIT"].includes(currentConfig.model);

    if (resolution.origin === "SPLIT_FALLBACK" && hasExplicitArenaModel && currentConfig.model !== "SPLIT") {
      return {
        success: true,
        applied: false,
        resolution,
        config: currentConfig,
        message: `Fallback SPLIT ignorado: arena ${arenaId} possui configuração comercial explícita (${currentConfig.model}).`,
      };
    }

    const isAlreadyApplied = Boolean(
      currentStored &&
      currentConfig.source === "ARENA" &&
      currentConfig.model === targetModel &&
      Number(currentConfig.monthlyFee || 0) === Number(targetMonthlyFee) &&
      (!targetPlanId || currentStored.commercialPlanId === targetPlanId)
    );

    if (isAlreadyApplied) {
      return {
        success: true,
        applied: false,
        resolution,
        config: currentConfig,
        message: `Idempotência mantida: arena ${arenaId} já está no estado comercial ${targetModel} (${targetPlanId}).`,
      };
    }

    // 5. Aplica através do writer canônico existente saveArenaFinancialConfig
    const userObj = {
      id: options?.user?.uid || options?.user?.id || "system",
      email: options?.user?.email || "system@hubesportivo.com",
      name: options?.user?.name || "System Commercial Cycle Resolver",
      role: "ADMIN",
      isAdmin: true,
      allowDirectChange: true,
    };

    const reason =
      resolution.origin === "SPLIT_FALLBACK"
        ? `Aplicação automática de SPLIT fallback: nenhum ciclo pago vigente para arena ${arenaId}`
        : `Aplicação do ciclo operacional vigente ${resolution.cycleId || ""} (${targetModel} - ${targetPlanId})`;

    const saveArgs: any[] = [
      arenaId,
      {
        financialModel: targetModel,
        commercialPlanId: targetPlanId,
        monthlyFee: targetMonthlyFee,
        commissionRate: targetCommissionRate,
        useCustomFinancialConfig: true,
        allowDirectChange: true,
      } as any,
      userObj,
      reason,
    ];
    if (options?.firestoreDb) {
      saveArgs.push(options.firestoreDb);
    }

    const saveResult = await (saveArenaFinancialConfig as any)(...saveArgs);

    return {
      success: true,
      applied: true,
      resolution,
      config: saveResult.config,
      message: reason,
    };
  }

  /**
   * BLOCO COMERCIAL 7C — Sincronização da Capacidade após Troca do Ciclo Operacional.
   */
  static syncOperationalCourtCapacity = syncOperationalCourtCapacity;

  /**
   * BLOCO COMERCIAL 7D — Orquestração Integrada do Ciclo Operacional e Capacidade.
   * Executa a cadeia canônica completa sem criar regras comerciais novas:
   * 1. resolveOperationalCommercialCycle (7A)
   * 2. applyOperationalCommercialCycle (7B)
   * 3. syncOperationalCourtCapacity (7C)
   * 4. Retorna o estado final integrado.
   */
  static async syncOperationalCommercialState(
    arenaId: string,
    options?: {
      now?: Date;
      user?: any;
      courts?: CourtItem[];
      firestoreDb?: any;
    }
  ): Promise<OperationalCommercialStateResult> {
    if (!arenaId || typeof arenaId !== "string" || !arenaId.trim()) {
      throw new Error("arenaId é obrigatório para sincronizar estado comercial operacional.");
    }

    // 1. Resolver o ciclo comercial operacional vigente
    const resolution = await resolveOperationalCommercialCycle(arenaId, options?.now);

    // 2. Aplicar o modelo/plano operacional resolvido
    const application = await SubscriptionService.applyOperationalCommercialCycle(arenaId, {
      now: options?.now,
      resolution,
      user: options?.user,
    });

    // 3. Sincronizar capacidade operacional das quadras
    const capacity = await syncOperationalCourtCapacity(arenaId, {
      resolution,
      courts: options?.courts,
      firestoreDb: options?.firestoreDb,
    });

    // 4. Retornar estado final integrado
    return {
      success: application.success && capacity.success,
      arenaId,
      resolution,
      application: {
        applied: application.applied,
        config: application.config,
        message: application.message,
      },
      capacity,
    };
  }

  /**
   * Helper para manipular memória em testes.
   */
  static _setInMemorySubscription(sub: SubscriptionRecord): void {
    inMemorySubscriptions.set(sub.id, sub);
  }

  static _setInMemorySubscriptionForTesting(sub: SubscriptionRecord): void {
    inMemorySubscriptions.set(sub.id, sub);
  }

  static getInMemorySubscription(subIdOrArenaId: string): SubscriptionRecord | undefined {
    if (inMemorySubscriptions.has(subIdOrArenaId)) {
      return inMemorySubscriptions.get(subIdOrArenaId);
    }
    for (const sub of inMemorySubscriptions.values()) {
      if (sub.arenaId === subIdOrArenaId) return sub;
    }
    return undefined;
  }

  static getSubscriptionPayments(subId: string): SubscriptionPaymentRecord[] {
    return inMemorySubscriptionPayments.get(subId) || [];
  }

  static _setInMemoryPaymentsForTesting(subId: string, payments: SubscriptionPaymentRecord[]): void {
    inMemorySubscriptionPayments.set(subId, payments);
  }

  static _setInMemoryCyclesForTesting(subId: string, cycles: SubscriptionCycleRecord[]): void {
    inMemorySubscriptionCycles.set(subId, cycles);
  }

  static _getInMemorySubscriptions(): Map<string, SubscriptionRecord> {
    return inMemorySubscriptions;
  }

  static _getInMemoryCycles(): Map<string, SubscriptionCycleRecord[]> {
    return inMemorySubscriptionCycles;
  }

  /**
   * Helper para limpar memória (útil para testes unitários).
   */
  static _resetMemory(): void {
    this._resetMemoryState();
  }

  static _resetMemoryState(): void {
    inMemorySubscriptions.clear();
    inMemorySubscriptionPayments.clear();
    inMemorySubscriptionCycles.clear();
    inMemoryConfirmedPDVPayments.clear();
    processedWebhookEventIds.clear();
    processedRenewalPaymentIds.clear();
  }
}

export interface OperationalCommercialStateResult {
  success: boolean;
  arenaId: string;
  resolution: OperationalCycleResolution;
  application: {
    applied: boolean;
    config?: any;
    message: string;
  };
  capacity: OperationalCapacitySyncResult;
}

export async function syncOperationalCommercialState(
  arenaId: string,
  options?: {
    now?: Date;
    user?: any;
    courts?: CourtItem[];
    firestoreDb?: any;
  }
): Promise<OperationalCommercialStateResult> {
  return SubscriptionService.syncOperationalCommercialState(arenaId, options);
}


